/* ==========================================================================
   Anfa Wen — theme.js (global storefront behavior)
   Phase 1: drawer management (nav drawer), basic helpers.
   Later phases extend this file (header, cart, predictive search, variants).
   No dependencies. Deferred in layout/theme.liquid.
   ========================================================================== */
(function () {
  'use strict';

  var AnfaWen = (window.AnfaWen = window.AnfaWen || {});

  /* JSON fetch helper (defined first: used during startup) */
  AnfaWen.getJSON = function (url, options) {
    options = options || {};
    options.headers = Object.assign({ Accept: 'application/json' }, options.headers || {});
    return fetch(url, options).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        if (!res.ok) throw data;
        return data;
      });
    });
  };

  /* ------------------------------------------------------------------
     Drawers — generic open/close with focus handling and ESC support.
     Triggers:  [data-drawer-toggle="id"]
     Closers:   [data-drawer-close] inside the drawer, overlay click, ESC
     Drawers:   .drawer with matching id, aria-hidden managed.
  ------------------------------------------------------------------ */
  var overlay = document.querySelector('[data-overlay]');
  var openDrawerEl = null;
  var lastFocused = null;

  function getFocusable(container) {
    if (!container) return [];
    return Array.prototype.filter.call(
      container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetParent !== null; }
    );
  }

  function setExpanded(drawer, expanded) {
    if (!drawer.id) return;
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-drawer-toggle="' + drawer.id + '"], [aria-controls="' + drawer.id + '"]'),
      function (el) { el.setAttribute('aria-expanded', expanded ? 'true' : 'false'); }
    );
  }

  function openDrawer(drawer, opener) {
    if (!drawer || drawer === openDrawerEl) return;
    if (openDrawerEl) closeDrawer(openDrawerEl, true);
    lastFocused = opener || document.activeElement;
    openDrawerEl = drawer;
    drawer.classList.add('drawer--open');
    drawer.setAttribute('aria-hidden', 'false');
    setExpanded(drawer, true);
    if (overlay) {
      overlay.hidden = false;
      /* force reflow so the transition runs */
      void overlay.offsetWidth;
      overlay.setAttribute('data-visible', '');
    }
    document.body.style.overflow = 'hidden';
    var focusable = getFocusable(drawer);
    if (focusable.length) focusable[0].focus();
  }

  function closeDrawer(drawer, silent) {
    if (!drawer) return;
    drawer.classList.remove('drawer--open');
    drawer.setAttribute('aria-hidden', 'true');
    setExpanded(drawer, false);
    if (openDrawerEl === drawer) openDrawerEl = null;
    if (!openDrawerEl) {
      if (overlay) {
        overlay.removeAttribute('data-visible');
        window.setTimeout(function () { if (!openDrawerEl) overlay.hidden = true; }, 320);
      }
      document.body.style.overflow = '';
    }
    if (!silent && lastFocused && document.contains(lastFocused)) lastFocused.focus();
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-drawer-toggle]');
    if (toggle) {
      var drawer = document.getElementById(toggle.getAttribute('data-drawer-toggle') || toggle.getAttribute('aria-controls'));
      if (drawer) {
        event.preventDefault();
        if (drawer.classList.contains('drawer--open')) closeDrawer(drawer);
        else openDrawer(drawer, toggle);
      }
      return;
    }
    var closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      var host = closer.closest('.drawer');
      if (host) closeDrawer(host);
      return;
    }
    if (overlay && event.target === overlay && openDrawerEl) closeDrawer(openDrawerEl);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && openDrawerEl) closeDrawer(openDrawerEl);
    /* rudimentary focus trap while a drawer is open */
    if (event.key === 'Tab' && openDrawerEl) {
      var focusable = getFocusable(openDrawerEl);
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  /* ------------------------------------------------------------------
     Quantity steppers ([data-quantity-input])
  ------------------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    var minus = event.target.closest('[data-qty-minus]');
    var plus = event.target.closest('[data-qty-plus]');
    if (!minus && !plus) return;
    var wrap = (minus || plus).closest('[data-quantity-input]');
    var input = wrap && wrap.querySelector('input');
    if (!input || input.disabled) return;
    var step = Number(input.step) || 1;
    var min = input.min !== '' ? Number(input.min) : 0;
    var value = Number(input.value) || 0;
    value = plus ? value + step : Math.max(min, value - step);
    input.value = value;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ------------------------------------------------------------------
     Header dropdowns (<details data-dropdown>): hover-open on pointer
     devices, close on outside click / Escape.
  ------------------------------------------------------------------ */
  var dropdowns = Array.prototype.slice.call(document.querySelectorAll('[data-dropdown]'));
  var canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  dropdowns.forEach(function (dropdown) {
    var parentItem = dropdown.closest('.header__menu-item');
    parentItem.addEventListener('mouseenter', function () {
      if (!canHover.matches) return;
      closeAllDropdowns(dropdown);
      dropdown.setAttribute('open', '');
    });
    parentItem.addEventListener('mouseleave', function () {
      if (!canHover.matches) return;
      dropdown.removeAttribute('open');
    });
    dropdown.addEventListener('toggle', function () {
      if (dropdown.open) closeAllDropdowns(dropdown);
    });
  });

  function closeAllDropdowns(except) {
    dropdowns.forEach(function (d) {
      if (d !== except) d.removeAttribute('open');
    });
  }

  document.addEventListener('click', function (event) {
    if (!event.target.closest('[data-dropdown]')) closeAllDropdowns(null);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeAllDropdowns(null);
  });

  /* ------------------------------------------------------------------
     Sticky header shadow
  ------------------------------------------------------------------ */
  var stickyHeader = document.querySelector('[data-sticky-header]');
  if (stickyHeader) {
    var onScroll = function () {
      stickyHeader.classList.toggle('header--scrolled', window.scrollY > 8);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ------------------------------------------------------------------
     Localization selects auto-submit Shopify's localization form
  ------------------------------------------------------------------ */
  document.addEventListener('change', function (event) {
    if (!event.target.matches('[data-localization-select]')) return;
    /* Language: go straight to the same page under the language's URL
       (works in theme previews too). The form POST remains the fallback. */
    var option = event.target.matches('[data-language-select]') ? event.target.selectedOptions[0] : null;
    var url = option && option.getAttribute('data-url');
    if (url) {
      window.location.href = url + window.location.search + window.location.hash;
      return;
    }
    var form = event.target.closest('form');
    if (form) form.submit();
  });

  /* ------------------------------------------------------------------
     Facets: auto-submit the filter/sort form (no-JS fallback = button)
  ------------------------------------------------------------------ */
  var facetForm = document.querySelector('[data-facet-form]');
  if (facetForm) {
    var facetTimer = null;
    facetForm.addEventListener('change', function (event) {
      if (!event.target.matches('input, select')) return;
      window.clearTimeout(facetTimer);
      facetTimer = window.setTimeout(function () {
        facetForm.submit();
      }, event.target.type === 'number' ? 600 : 250);
    });
  }

  /* ------------------------------------------------------------------
     Pagination: preserve filter/sort query params across page links
  ------------------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    var link = event.target.closest('.pagination a');
    if (!link) return;
    try {
      var url = new URL(link.href, window.location.href);
      var current = new URL(window.location.href);
      current.searchParams.forEach(function (value, key) {
        if (key !== 'page' && !url.searchParams.has(key)) url.searchParams.set(key, value);
      });
      event.preventDefault();
      window.location.href = url.href;
    } catch (err) {
      /* fall back to default navigation */
    }
  });

  /* ------------------------------------------------------------------
     Header search (inline): live product results under the field via
     Shopify's predictive search + section rendering. Desktop: field in
     the header; mobile: the search icon slides the field open.
  ------------------------------------------------------------------ */
  var headerSearch = document.querySelector('[data-header-search]');
  if (headerSearch) {
    var predictiveForm = headerSearch.querySelector('[data-predictive-search]');
    var pInput = headerSearch.querySelector('[data-predictive-search-input]');
    var pPanel = headerSearch.querySelector('[data-search-panel]');
    var pSuggest = headerSearch.querySelector('[data-search-suggest]');
    var pResults = headerSearch.querySelector('[data-predictive-search-results]');
    var pClear = headerSearch.querySelector('[data-search-clear]');
    var pUrl = predictiveForm.getAttribute('data-predictive-url');
    var searchToggle = document.querySelector('[data-search-toggle]');
    var headerEl = headerSearch.closest('.header');
    var pTimer = null;
    var pController = null;

    var openPanel = function () {
      pPanel.hidden = false;
      pInput.setAttribute('aria-expanded', 'true');
      headerSearch.classList.add('is-open');
    };
    var closeSearch = function () {
      pPanel.hidden = true;
      pInput.setAttribute('aria-expanded', 'false');
      headerSearch.classList.remove('is-open');
      if (headerEl) headerEl.classList.remove('header--search-open');
      if (searchToggle) searchToggle.setAttribute('aria-expanded', 'false');
    };
    var showSuggestions = function (show) {
      pSuggest.hidden = !show;
      if (show) pResults.innerHTML = '';
    };

    pInput.addEventListener('focus', openPanel);
    pInput.addEventListener('input', function () {
      var query = pInput.value.trim();
      pClear.hidden = !pInput.value;
      openPanel();
      window.clearTimeout(pTimer);
      if (query.length < 2) { showSuggestions(true); return; }
      pTimer = window.setTimeout(function () {
        if (pController) pController.abort();
        pController = new AbortController();
        pResults.setAttribute('aria-busy', 'true');
        fetch(pUrl + '?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=6&section_id=predictive-search', { signal: pController.signal })
          .then(function (res) {
            if (!res.ok) throw new Error('predictive search failed');
            return res.text();
          })
          .then(function (text) {
            var rendered = new DOMParser().parseFromString(text, 'text/html').querySelector('.shopify-section');
            pSuggest.hidden = true;
            pResults.innerHTML = rendered ? rendered.innerHTML : '';
          })
          .catch(function (err) {
            if (err && err.name !== 'AbortError') showSuggestions(true);
          })
          .then(function () { pResults.removeAttribute('aria-busy'); });
      }, 250);
    });
    pClear.addEventListener('click', function () {
      pInput.value = '';
      pClear.hidden = true;
      showSuggestions(true);
      pInput.focus();
    });
    if (searchToggle) {
      searchToggle.addEventListener('click', function () {
        var opening = !headerEl.classList.contains('header--search-open');
        if (!opening) { closeSearch(); return; }
        headerEl.classList.add('header--search-open');
        searchToggle.setAttribute('aria-expanded', 'true');
        window.setTimeout(function () { pInput.focus(); }, 60);
      });
    }
    document.addEventListener('click', function (event) {
      if (headerSearch.contains(event.target) || (searchToggle && searchToggle.contains(event.target))) return;
      if (!pPanel.hidden || (headerEl && headerEl.classList.contains('header--search-open'))) closeSearch();
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && headerSearch.classList.contains('is-open')) {
        closeSearch();
        pInput.blur();
      }
    });
  }

  /* ------------------------------------------------------------------
     Cart (AJAX via Shopify's native endpoints)
       - add:      POST /cart/add.js      (product form + quick add)
       - change:   POST /cart/change.js   (quantity / remove)
       - note:     POST /cart/update.js
     After every mutation the drawer/cart-page sections are re-rendered
     through the section rendering API, so displayed state always
     reflects Shopify's actual cart.
  ------------------------------------------------------------------ */
  function parseSectionHTML(text) {
    return new DOMParser().parseFromString(text, 'text/html');
  }

  function refreshCartSections() {
    var tasks = [];
    var drawerEl = document.getElementById('CartDrawer');
    if (drawerEl) {
      tasks.push(
        fetch(AnfaWen.routes.cart + '?section_id=cart-drawer', { headers: { Accept: 'text/html' } })
          .then(function (res) { return res.text(); })
          .then(function (text) {
            var fresh = parseSectionHTML(text).querySelector('#CartDrawer');
            if (!fresh) return;
            ['data-cart-drawer-body', 'data-cart-drawer-footer'].forEach(function (attr) {
              var oldEl = document.querySelector('[' + attr + ']');
              var newEl = fresh.querySelector('[' + attr + ']');
              if (!oldEl) return;
              oldEl.innerHTML = newEl ? newEl.innerHTML : '';
              /* Footer (subtotal + checkout) is hidden while the cart is empty */
              if (attr === 'data-cart-drawer-footer') {
                oldEl.hidden = !newEl || newEl.hasAttribute('hidden');
                initDelivery(oldEl);
              }
            });
          })
          .catch(function () {})
      );
    }
    if (document.querySelector('[data-cart-page-section]')) {
      tasks.push(
        fetch(AnfaWen.routes.cart + '?section_id=main-cart', { headers: { Accept: 'text/html' } })
          .then(function (res) { return res.text(); })
          .then(function (text) {
            var fresh = parseSectionHTML(text).querySelector('[data-cart-page-section]');
            var current = document.querySelector('[data-cart-page-section]');
            if (fresh && current) current.innerHTML = fresh.innerHTML;
          })
          .catch(function () {})
      );
    }
    tasks.push(
      AnfaWen.getJSON(AnfaWen.routes.cart + '.js')
        .then(function (cart) { AnfaWen.updateCartCount(cart.item_count); })
        .catch(function () {})
    );
    return Promise.all(tasks);
  }

  function showCartStatus(message, isError) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-cart-status]'), function (el) {
      el.textContent = message || '';
      el.classList.toggle('form__message--error', !!isError && !!message);
    });
  }

  function cartChange(key, quantity) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-cart-item]'), function (el) {
      var input = el.querySelector('[data-line-key="' + key + '"]');
      if (input) el.classList.add('cart-item--loading');
    });
    return fetch(AnfaWen.routes.cartChange + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw data;
          return data;
        });
      })
      .then(function () { return refreshCartSections(); })
      .catch(function (error) {
        showCartStatus((error && (error.description || error.message)) || 'Cart error', true);
        return refreshCartSections();
      });
  }

  AnfaWen.refreshCart = refreshCartSections;
  AnfaWen.cartChange = cartChange;

  AnfaWen.openCartAfterAdd = function () {
    /* The product form announces the add itself; the card grid, shop-the-look
       and the bundle all come through here and announced nothing, so a screen
       reader got no confirmation. One place covers all three. The message has
       to go in after the refresh, which replaces the drawer body it lives in. */
    var announce = function () {
      showCartStatus((AnfaWen.strings && AnfaWen.strings.added) || '');
    };
    var drawerEl = document.getElementById('CartDrawer');
    if (!drawerEl) return refreshCartSections().then(announce);
    return refreshCartSections().then(function () {
      openDrawer(drawerEl);
      announce();
    });
  };

  /* Remove items (links keep url_to_remove as no-JS fallback) */
  document.addEventListener('click', function (event) {
    var removeLink = event.target.closest('[data-cart-remove]');
    if (!removeLink) return;
    event.preventDefault();
    var item = removeLink.closest('[data-cart-item]');
    if (item) item.classList.add('cart-item--loading');
    cartChange(removeLink.getAttribute('data-line-key'), 0);
  });

  /* Quantity changes on cart line items */
  document.addEventListener('change', function (event) {
    var input = event.target.closest('input[data-line-key]');
    if (input) {
      cartChange(input.getAttribute('data-line-key'), parseInt(input.value, 10) || 0);
      return;
    }
    var note = event.target.closest('[data-cart-note]');
    if (note) {
      fetch(AnfaWen.routes.cartUpdate + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: note.value })
      }).catch(function () {});
    }
  });

  /* Quick add on product cards (native POST remains the fallback) */
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-quick-add-form]');
    if (!form || !window.fetch || !window.FormData) return;
    event.preventDefault();
    /* Buy now: cart permalink goes straight to checkout with just this item, in the shopper's language */
    if (event.submitter && event.submitter.hasAttribute('data-card-buy')) {
      var variantInput = form.querySelector('[name="id"]');
      event.submitter.setAttribute('aria-busy', 'true');
      window.location.href = AnfaWen.routes.cart + '/' + variantInput.value + ':1';
      return;
    }
    var btn = form.querySelector('[data-card-add]') || form.querySelector('button');
    /* aria-busy was set but never read, so a fast double tap sent two adds */
    if (form.getAttribute('data-adding') === 'true') return;
    form.setAttribute('data-adding', 'true');
    if (btn) btn.setAttribute('aria-busy', 'true');
    fetch(form.getAttribute('action') + '.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw data;
          return data;
        });
      })
      .then(function () { return AnfaWen.openCartAfterAdd(); })
      .catch(function (error) {
        showCartStatus((error && (error.description || error.message)) || 'Cart error', true);
      })
      .then(function () {
        form.removeAttribute('data-adding');
        if (btn) btn.removeAttribute('aria-busy');
      });
  });

  /* ------------------------------------------------------------------
     Native <dialog>s: [data-dialog-open="id"] opens, [data-dialog-close]
     or a backdrop click closes.
  ------------------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      var dialog = document.getElementById(opener.getAttribute('data-dialog-open'));
      if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
      return;
    }
    var closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      var host = closer.closest('dialog');
      if (host) host.close();
      return;
    }
    if (event.target.tagName === 'DIALOG' && event.target.open) event.target.close();
  });

  /* ------------------------------------------------------------------
     Announcement rotator: cycles messages, pauses on hover/focus
  ------------------------------------------------------------------ */
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  Array.prototype.forEach.call(document.querySelectorAll('[data-announcement-rotator]'), function (rotator) {
    var messages = rotator.querySelectorAll('.announcement-bar__message');
    if (messages.length < 2 || reducedMotion.matches) return;
    var index = 0;
    var paused = false;
    rotator.addEventListener('mouseenter', function () { paused = true; });
    rotator.addEventListener('mouseleave', function () { paused = false; });
    rotator.addEventListener('focusin', function () { paused = true; });
    rotator.addEventListener('focusout', function () { paused = false; });
    window.setInterval(function () {
      if (paused || document.hidden) return;
      messages[index].classList.remove('is-active');
      messages[index].setAttribute('aria-hidden', 'true');
      index = (index + 1) % messages.length;
      messages[index].classList.add('is-active');
      messages[index].removeAttribute('aria-hidden');
    }, 5000);
  });

  /* ------------------------------------------------------------------
     Scroll reveal: [data-reveal] fades up once it enters the viewport.
     Homepage product cards opt in automatically, staggered per row.
  ------------------------------------------------------------------ */
  Array.prototype.forEach.call(document.querySelectorAll('.template-index .grid--products'), function (grid) {
    Array.prototype.forEach.call(grid.children, function (card, i) {
      card.setAttribute('data-reveal', '');
      card.style.setProperty('--reveal-delay', (i % 4) * 70 + 'ms');
    });
  });
  var revealTargets = document.querySelectorAll('[data-reveal]');
  if ('IntersectionObserver' in window) {
    var revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    Array.prototype.forEach.call(revealTargets, function (el) { revealObserver.observe(el); });
  } else {
    Array.prototype.forEach.call(revealTargets, function (el) { el.classList.add('is-revealed'); });
  }

  /* ------------------------------------------------------------------
     Product card options: swatches/chips pick a variant, then the card's
     image, price, variant id and buttons follow it.
  ------------------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    var chip = event.target.closest('.card-opt__value');
    if (!chip) return;
    var card = chip.closest('[data-product-card]');
    var json = card && card.querySelector('[data-card-variants]');
    if (!json) return;
    var variants;
    try { variants = JSON.parse(json.textContent); } catch (e) { return; }

    Array.prototype.forEach.call(chip.parentNode.querySelectorAll('.card-opt__value'), function (el) {
      el.setAttribute('aria-pressed', el === chip ? 'true' : 'false');
    });
    var selected = Array.prototype.map.call(card.querySelectorAll('[data-card-option]'), function (group) {
      var pressed = group.querySelector('[aria-pressed="true"]');
      return pressed ? pressed.getAttribute('data-value') : null;
    });
    var index = Number(chip.parentNode.getAttribute('data-card-option'));
    var value = chip.getAttribute('data-value');
    function matches(v) {
      return selected.every(function (s, i) { return s === null || v.o[i] === s; });
    }
    /* Exact match first; otherwise keep the clicked value and take any available combination */
    var variant = variants.filter(matches)[0] ||
      variants.filter(function (v) { return v.o[index] === value && v.a; })[0] ||
      variants.filter(function (v) { return v.o[index] === value; })[0];
    if (!variant) return;

    /* Re-sync other groups to the resolved variant and grey out impossible values */
    Array.prototype.forEach.call(card.querySelectorAll('[data-card-option]'), function (group) {
      var gi = Number(group.getAttribute('data-card-option'));
      Array.prototype.forEach.call(group.querySelectorAll('.card-opt__value'), function (el) {
        var val = el.getAttribute('data-value');
        el.setAttribute('aria-pressed', variant.o[gi] === val ? 'true' : 'false');
        var possible = variants.some(function (v) {
          return v.a && v.o[gi] === val && v.o.every(function (o, oi) { return oi === gi || oi === index || o === variant.o[oi]; });
        });
        el.classList.toggle('is-unavailable', !possible);
      });
    });

    var idInput = card.querySelector('[data-card-variant-id]');
    if (idInput) idInput.value = variant.id;
    var addBtn = card.querySelector('[data-card-add]');
    var buyBtn = card.querySelector('[data-card-buy]');
    if (addBtn) {
      addBtn.disabled = !variant.a;
      var label = addBtn.querySelector('.card-actions__label');
      if (label) label.textContent = addBtn.getAttribute(variant.a ? 'data-label-add' : 'data-label-sold-out');
    }
    if (buyBtn) buyBtn.disabled = !variant.a;

    var priceWrap = card.querySelector('[data-card-price] .price');
    if (priceWrap) {
      priceWrap.textContent = '';
      var current = document.createElement('span');
      current.className = 'price__current';
      current.textContent = variant.p;
      priceWrap.appendChild(current);
      if (variant.c) {
        var compare = document.createElement('s');
        compare.className = 'price__compare';
        compare.textContent = variant.c;
        priceWrap.appendChild(compare);
      }
      priceWrap.classList.toggle('price--on-sale', !!variant.c);
      priceWrap.classList.toggle('price--sold-out', !variant.a);
    }
    AnfaWen.updatePerPiece(card, variant.pc);

    var img = card.querySelector('.product-card__image');
    if (img && variant.i && img.getAttribute('data-variant-src') !== variant.i) {
      /* Keep the chosen variant's photo visible (no second-image hover swap) */
      card.classList.add('has-variant-image');
      img.setAttribute('data-variant-src', variant.i);
      img.removeAttribute('srcset');
      img.classList.add('is-swapping');
      var next = new Image();
      next.onload = function () { img.src = variant.i; img.classList.remove('is-swapping'); };
      next.onerror = function () { img.classList.remove('is-swapping'); };
      next.src = variant.i;
    }
  });

  /* ------------------------------------------------------------------
     Top sellers: load best-selling cards via the Section Rendering API,
     skip the product being viewed, keep the configured count.
  ------------------------------------------------------------------ */
  Array.prototype.forEach.call(document.querySelectorAll('[data-top-sellers]'), function (wrap) {
    var grid = wrap.querySelector('[data-top-sellers-grid]');
    if (!grid || grid.querySelector('.top-sellers__item') || !window.fetch) return;
    var limit = Number(wrap.getAttribute('data-limit')) || 4;
    var exclude = wrap.getAttribute('data-exclude');
    fetch(wrap.getAttribute('data-url'))
      .then(function (res) {
        if (!res.ok) throw new Error('top sellers failed');
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var items = Array.prototype.filter.call(doc.querySelectorAll('.top-sellers__item'), function (item) {
          return item.getAttribute('data-product-id') !== exclude;
        }).slice(0, limit);
        if (!items.length) { wrap.hidden = true; return; }
        grid.textContent = '';
        items.forEach(function (item, i) {
          var rank = item.querySelector('.top-sellers__rank');
          if (rank) rank.textContent = String(i + 1);
          item.style.setProperty('--reveal-delay', i * 80 + 'ms');
          item.classList.add('top-sellers__item--in');
          grid.appendChild(document.importNode(item, true));
        });
      })
      .catch(function () { wrap.hidden = true; });
  });

  /* Delivery estimate: working-day range formatted in the page language.
     A function declaration so the cart refresh (above) can re-run it on new drawer markup. */
  function initDelivery(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-delivery-estimate]'), formatDelivery);
  }
  initDelivery(document);
  function formatDelivery(el) {
    var skipFriday = el.getAttribute('data-skip-friday') === 'true';
    function addWorkingDays(days) {
      var date = new Date();
      while (days > 0) {
        date.setDate(date.getDate() + 1);
        if (!(skipFriday && date.getDay() === 5)) days -= 1;
      }
      return date;
    }
    var lang = document.documentElement.lang || 'en';
    var fmt;
    try { fmt = new Intl.DateTimeFormat(lang, { weekday: 'short', day: 'numeric', month: 'short' }); } catch (e) { return; }
    var text = (el.getAttribute('data-template') || '')
      .replace('__FROM__', fmt.format(addWorkingDays(Number(el.getAttribute('data-min')) || 0)))
      .replace('__TO__', fmt.format(addWorkingDays(Number(el.getAttribute('data-max')) || 0)));
    var holder = el.querySelector('[data-delivery-text]');
    if (holder && text) {
      var tmp = document.createElement('textarea');
      tmp.innerHTML = text; /* template arrives HTML-escaped from the t filter */
      holder.textContent = tmp.value;
      el.hidden = false;
    }
  }

  /* ------------------------------------------------------------------
     Motion layer: ambient cotton/feathers, scroll progress, hero parallax,
     image reveals, word-by-word headings. Off for prefers-reduced-motion.
  ------------------------------------------------------------------ */
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var body = document.body;
  var rtl = document.documentElement.dir === 'rtl' ? -1 : 1;

  /* Headings: wrap words so they can rise in one after another (text nodes only; <br> kept) */
  if (!reduceMotion && body.classList.contains('has-text-reveal') && 'IntersectionObserver' in window) {
    var headingObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('words-in');
        headingObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px' });
    Array.prototype.forEach.call(document.querySelectorAll('.section-head__heading, .b2b-band__heading, .footer__cta-heading, .hero-ed__heading'), function (heading) {
      var index = 0;
      Array.prototype.slice.call(heading.childNodes).forEach(function (node) {
        if (node.nodeType !== 3 || !node.textContent.trim()) return;
        var fragment = document.createDocumentFragment();
        node.textContent.split(/(\s+)/).forEach(function (part) {
          if (!part) return;
          if (/^\s+$/.test(part)) { fragment.appendChild(document.createTextNode(part)); return; }
          var outer = document.createElement('span');
          outer.className = 'word';
          var inner = document.createElement('span');
          inner.textContent = part;
          inner.style.setProperty('--i', index++);
          outer.appendChild(inner);
          fragment.appendChild(outer);
        });
        node.parentNode.replaceChild(fragment, node);
      });
      heading.classList.add('words-ready');
      headingObserver.observe(heading);
    });
  }

  /* Image reveals: section photos unveil (clip) as they enter */
  var revealMedia = [];
  if (!reduceMotion && body.classList.contains('has-parallax') && 'IntersectionObserver' in window) {
    var mediaObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('media-in');
        mediaObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -12% 0px' });
    revealMedia = document.querySelectorAll('.category-tile__media, .brand-intro__media .media, .b2b-band__media, .look__media, .collection-card .media, .collection-banner .media');
    Array.prototype.forEach.call(revealMedia, function (el) {
      el.classList.add('media-reveal');
      mediaObserver.observe(el);
    });
  }

  var ambientItems = [];
  var ambientLayers = document.querySelectorAll('[data-ambient]');
  if (ambientLayers.length && !reduceMotion) {
    ambientItems = Array.prototype.map.call(document.querySelectorAll('[data-ambient] .ambient__item'), function (el) {
      return {
        el: el,
        speed: Number(el.getAttribute('data-speed')) || 0.4,
        base: Math.random() * window.innerHeight,
        fall: 8 + Math.random() * 14,            /* px per second of idle drift */
        amp: 14 + Math.random() * 26,            /* side sway */
        period: 3200 + Math.random() * 3800,
        phase: Math.random() * Math.PI * 2,
        rot: Math.random() * 60 - 30
      };
    });
    Array.prototype.forEach.call(ambientLayers, function (layer) { layer.classList.add('is-live'); });
  }

  var progress = document.querySelector('[data-scroll-progress]');
  var heroImg = (!reduceMotion && body.classList.contains('has-parallax')) ? document.querySelector('.hero-ed__media--desktop img, .hero__media img') : null;
  var lastScroll = window.scrollY;
  var velocity = 0;
  var startTime = null;

  function motionFrame(now) {
    if (startTime === null) startTime = now;
    var scrollY = window.scrollY;
    velocity = velocity * 0.88 + (scrollY - lastScroll) * 0.12;
    lastScroll = scrollY;

    if (progress) {
      var max = document.documentElement.scrollHeight - window.innerHeight;
      progress.style.transform = 'scaleX(' + (max > 0 ? Math.min(1, scrollY / max) : 0) + ')';
    }
    if (heroImg && scrollY < window.innerHeight * 1.2) {
      heroImg.style.translate = '0 ' + Math.min(scrollY * 0.1, 48).toFixed(1) + 'px'; /* capped: the image is pre-scaled in CSS so no gap opens */
    }
    if (ambientItems.length) {
      var span = window.innerHeight + 160;
      var t = (now - startTime) / 1000;
      var gust = Math.max(-40, Math.min(40, velocity * 1.6));
      ambientItems.forEach(function (p) {
        var y = (p.base + t * p.fall - scrollY * p.speed) % span;
        if (y < 0) y += span;
        y -= 80;
        var sway = Math.sin(now / p.period + p.phase);
        var x = sway * p.amp + gust * p.speed * 1.4 * rtl;
        var r = p.rot + sway * 14 + gust * p.speed * 0.9 * rtl;
        p.el.style.transform = 'translate3d(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px,0) rotate(' + r.toFixed(1) + 'deg)';
      });
    }
    if (progress || heroImg || ambientItems.length) window.requestAnimationFrame(motionFrame);
  }
  if (progress || heroImg || ambientItems.length) window.requestAnimationFrame(motionFrame);

  /* ------------------------------------------------------------------
     Wishlist: handles in localStorage ("aw-wishlist"); hearts on cards
     and the product page, count badges, drawer rows built from
     /products/<handle>.js with textContent only.
  ------------------------------------------------------------------ */
  var WISH_KEY = 'aw-wishlist';
  var strings = AnfaWen.strings || {};
  function readWishlist() {
    try {
      var list = JSON.parse(window.localStorage.getItem(WISH_KEY) || '[]');
      return Array.isArray(list) ? list.filter(function (h) { return typeof h === 'string'; }) : [];
    } catch (e) { return []; }
  }
  function writeWishlist(list) {
    try { window.localStorage.setItem(WISH_KEY, JSON.stringify(list.slice(0, 50))); } catch (e) { /* storage blocked */ }
  }
  function syncWishlistUI() {
    var list = readWishlist();
    Array.prototype.forEach.call(document.querySelectorAll('[data-wishlist-toggle]'), function (btn) {
      var saved = list.indexOf(btn.getAttribute('data-handle')) !== -1;
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
      var label = btn.querySelector('[data-wishlist-label]');
      if (label) label.textContent = btn.getAttribute(saved ? 'data-label-remove' : 'data-label-add');
    });
    Array.prototype.forEach.call(document.querySelectorAll('[data-wishlist-count]'), function (el) {
      el.textContent = list.length ? String(list.length) : '';
    });
  }
  AnfaWen.formatMoney = function (cents, format) {
    format = format || AnfaWen.moneyFormat || '{{amount}}';
    var value = (Number(cents) || 0) / 100;
    function group(num, decimals, thousands, decimal) {
      var parts = num.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (m, key) {
      switch (key) {
        case 'amount_no_decimals': return group(value, 0, ',', '.');
        case 'amount_with_comma_separator': return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return group(value, 2, "'", '.');
        default: return group(value, 2, ',', '.');
      }
    }).replace(/<[^>]*>/g, '');
  };

  var wishList = document.querySelector('[data-wishlist-list]');
  var wishEmpty = document.querySelector('[data-wishlist-empty]');
  function productUrl(handle) {
    return (AnfaWen.routes.root || '/').replace(/\/$/, '') + '/products/' + encodeURIComponent(handle);
  }
  function renderWishlist() {
    if (!wishList) return;
    var list = readWishlist();
    if (wishEmpty) wishEmpty.hidden = list.length > 0;
    wishList.textContent = '';
    list.forEach(function (handle) {
      var li = document.createElement('li');
      li.className = 'wishlist-item is-loading';
      wishList.appendChild(li);
      AnfaWen.getJSON(productUrl(handle) + '.js')
        .then(function (product) {
          li.classList.remove('is-loading');
          var variant = product.variants.filter(function (v) { return v.available; })[0] || product.variants[0];
          var link = document.createElement('a');
          link.href = productUrl(handle);
          link.className = 'wishlist-item__media';
          if (product.featured_image) {
            var img = document.createElement('img');
            img.src = product.featured_image + (product.featured_image.indexOf('?') === -1 ? '?' : '&') + 'width=200';
            img.alt = '';
            img.loading = 'lazy';
            link.appendChild(img);
          }
          var info = document.createElement('div');
          info.className = 'wishlist-item__info';
          var title = document.createElement('a');
          title.href = productUrl(handle);
          title.className = 'wishlist-item__title';
          title.textContent = product.title;
          var price = document.createElement('span');
          price.className = 'wishlist-item__price';
          price.textContent = (product.price_varies ? '' : '') + AnfaWen.formatMoney(product.price_min);
          var actions = document.createElement('div');
          actions.className = 'wishlist-item__actions';
          if (product.available && product.variants.length === 1) {
            var add = document.createElement('button');
            add.type = 'button';
            add.className = 'btn btn--primary btn--sm';
            add.textContent = strings.addToCart || 'Add to cart';
            add.addEventListener('click', function () {
              add.setAttribute('aria-busy', 'true');
              fetch(AnfaWen.routes.cartAdd + '.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] })
              }).then(function () { return AnfaWen.openCartAfterAdd(); })
                .catch(function () {})
                .then(function () { add.removeAttribute('aria-busy'); });
            });
            actions.appendChild(add);
          } else {
            var choose = document.createElement('a');
            choose.href = productUrl(handle);
            choose.className = 'btn btn--secondary btn--sm';
            choose.textContent = product.available ? (strings.chooseOptions || 'Choose options') : (strings.soldOut || 'Sold out');
            actions.appendChild(choose);
          }
          var remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'wishlist-item__remove';
          remove.setAttribute('aria-label', (strings.remove || 'Remove') + ': ' + product.title);
          remove.textContent = '×';
          remove.addEventListener('click', function () {
            writeWishlist(readWishlist().filter(function (h) { return h !== handle; }));
            li.classList.add('is-leaving');
            window.setTimeout(function () { syncWishlistUI(); renderWishlist(); }, 220);
          });
          info.appendChild(title);
          info.appendChild(price);
          info.appendChild(actions);
          li.appendChild(link);
          li.appendChild(info);
          li.appendChild(remove);
        })
        .catch(function () {
          /* Product removed from the store: drop it quietly */
          writeWishlist(readWishlist().filter(function (h) { return h !== handle; }));
          li.remove();
          syncWishlistUI();
          if (wishEmpty) wishEmpty.hidden = readWishlist().length > 0;
        });
    });
  }
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-wishlist-toggle]');
    if (btn) {
      event.preventDefault();
      var handle = btn.getAttribute('data-handle');
      var list = readWishlist();
      var at = list.indexOf(handle);
      if (at === -1) list.unshift(handle); else list.splice(at, 1);
      writeWishlist(list);
      syncWishlistUI();
      if (AnfaWen.toast) AnfaWen.toast(at === -1 ? strings.toastSaved : strings.toastRemoved);
      if (at === -1) {
        btn.classList.remove('is-popping');
        void btn.offsetWidth;
        btn.classList.add('is-popping');
        Array.prototype.forEach.call(document.querySelectorAll('[data-wishlist-count]'), function (el) {
          el.classList.remove('is-bumped');
          void el.offsetWidth;
          el.classList.add('is-bumped');
        });
      }
      return;
    }
    if (event.target.closest('[data-drawer-toggle="WishlistDrawer"]')) renderWishlist();
  });
  window.addEventListener('storage', function (e) { if (e.key === WISH_KEY) syncWishlistUI(); });
  syncWishlistUI();
  /* Cards loaded later (top sellers, recommendations) */
  if ('MutationObserver' in window) {
    var wishTimer = null;
    new MutationObserver(function () {
      window.clearTimeout(wishTimer);
      wishTimer = window.setTimeout(syncWishlistUI, 60);
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* Bottom nav search: open the header search */
  document.addEventListener('click', function (event) {
    if (!event.target.closest('[data-bottom-search]')) return;
    var toggle = document.querySelector('[data-search-toggle]');
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    if (toggle && !document.querySelector('.header--search-open')) toggle.click();
  });

  /* ------------------------------------------------------------------
     Frequently bought together (product page block): this item +
     Shopify recommendations, checkboxes, live total, add all at once
  ------------------------------------------------------------------ */
  var fbt = document.querySelector('[data-fbt]');
  if (fbt && window.fetch) {
    var productSection = fbt.closest('[data-product-section]');
    var fbtList = fbt.querySelector('[data-fbt-list]');
    var fbtTotal = fbt.querySelector('[data-fbt-total]');
    var fbtAdd = fbt.querySelector('[data-fbt-add]');
    var fbtLabel = fbt.querySelector('[data-fbt-add-label]');
    var currentVariants = [];
    try { currentVariants = JSON.parse(productSection.querySelector('[data-variant-json]').textContent); } catch (e) { currentVariants = []; }
    function currentVariant() {
      var input = productSection.querySelector('[data-product-form] [name="id"]');
      var id = input ? String(input.value) : '';
      return currentVariants.filter(function (v) { return String(v.id) === id; })[0] || currentVariants[0];
    }
    function updateFbt() {
      var total = 0;
      var count = 0;
      Array.prototype.forEach.call(fbtList.querySelectorAll('input[type="checkbox"]'), function (box) {
        var row = box.closest('.fbt__item');
        if (box.hasAttribute('data-current')) {
          var v = currentVariant();
          box.value = v ? v.id : box.value;
          box.setAttribute('data-price', v ? v.priceCents : 0);
          var priceEl = row.querySelector('.fbt__price');
          if (priceEl && v) priceEl.textContent = AnfaWen.formatMoney(v.priceCents);
        }
        row.classList.toggle('is-off', !box.checked);
        if (box.checked) { total += Number(box.getAttribute('data-price')) || 0; count += 1; }
      });
      fbtTotal.textContent = AnfaWen.formatMoney(total);
      fbtLabel.textContent = (fbtLabel.getAttribute('data-template') || '').replace('__N__', count);
      fbtAdd.disabled = count === 0;
    }
    function fbtRow(opts) {
      var li = document.createElement('li');
      li.className = 'fbt__item';
      var label = document.createElement('label');
      label.className = 'fbt__check';
      var box = document.createElement('input');
      box.type = 'checkbox';
      box.checked = true;
      box.value = opts.id;
      box.setAttribute('data-price', opts.price);
      if (opts.current) box.setAttribute('data-current', '');
      var mark = document.createElement('span');
      mark.className = 'fbt__mark';
      label.appendChild(box);
      label.appendChild(mark);
      var media = document.createElement(opts.url ? 'a' : 'span');
      media.className = 'fbt__media';
      if (opts.url) media.href = opts.url;
      if (opts.image) {
        var img = document.createElement('img');
        img.src = opts.image;
        img.alt = '';
        img.loading = 'lazy';
        media.appendChild(img);
      }
      var info = document.createElement('div');
      info.className = 'fbt__info';
      var title = document.createElement(opts.url ? 'a' : 'span');
      title.className = 'fbt__title';
      if (opts.url) title.href = opts.url;
      title.textContent = opts.title;
      var price = document.createElement('span');
      price.className = 'fbt__price';
      price.textContent = AnfaWen.formatMoney(opts.price);
      info.appendChild(title);
      info.appendChild(price);
      li.appendChild(label);
      li.appendChild(media);
      li.appendChild(info);
      return li;
    }
    var limit = Number(fbt.getAttribute('data-limit')) || 2;
    var recUrl = (AnfaWen.routes.recommendations || '/recommendations/products') + '.json?product_id=' + fbt.getAttribute('data-product-id') + '&limit=' + (limit + 3) + '&intent=related';
    var currentId = String(fbt.getAttribute('data-product-id'));
    var currentType = fbt.getAttribute('data-product-type') || '';
    /* /collections/all/products.json uses decimal-string prices and images[]: normalise to the .js shape */
    function normaliseCatalog(p) {
      return {
        id: p.id,
        title: p.title,
        url: (AnfaWen.routes.root || '/').replace(/\/$/, '') + '/products/' + p.handle,
        product_type: p.product_type,
        featured_image: p.images && p.images[0] ? p.images[0].src : '',
        available: p.variants.some(function (v) { return v.available; }),
        variants: p.variants.map(function (v) { return { id: v.id, available: v.available, price: Math.round(parseFloat(v.price) * 100) }; })
      };
    }
    AnfaWen.getJSON(recUrl)
      .then(function (data) {
        var recs = (data.products || []).filter(function (p) { return p.available; });
        if (recs.length) return recs;
        /* New stores have no recommendation data yet: fall back to the catalog, same product type first */
        return AnfaWen.getJSON((AnfaWen.routes.root || '/').replace(/\/$/, '') + '/collections/all/products.json?limit=20').then(function (catalog) {
          var items = (catalog.products || []).map(normaliseCatalog).filter(function (p) { return p.available && String(p.id) !== currentId; });
          return items.filter(function (p) { return p.product_type === currentType; })
            .concat(items.filter(function (p) { return p.product_type !== currentType; }));
        });
      })
      .then(function (recs) {
        recs = (recs || []).slice(0, limit);
        if (!recs.length) return;
        var v = currentVariant();
        var mainImg = productSection.querySelector('[data-media-item]:not(.hidden) img');
        var titleEl = productSection.querySelector('.product__title');
        fbtList.appendChild(fbtRow({
          id: v ? v.id : '',
          price: v ? v.priceCents : 0,
          title: (strings.fbtThisItem || 'This item') + ': ' + (titleEl ? titleEl.textContent.trim() : ''),
          image: mainImg ? mainImg.currentSrc || mainImg.src : '',
          current: true
        }));
        recs.forEach(function (p) {
          var variant = p.variants.filter(function (x) { return x.available; })[0];
          fbtList.appendChild(fbtRow({
            id: variant.id,
            price: variant.price,
            title: p.title,
            url: p.url,
            image: p.featured_image ? p.featured_image + (p.featured_image.indexOf('?') === -1 ? '?' : '&') + 'width=160' : ''
          }));
        });
        fbt.hidden = false;
        updateFbt();
        fbtList.addEventListener('change', updateFbt);
        productSection.addEventListener('change', function (e) { if (!fbt.contains(e.target)) window.setTimeout(updateFbt, 0); });
        fbtAdd.addEventListener('click', function () {
          var items = Array.prototype.filter.call(fbtList.querySelectorAll('input[type="checkbox"]'), function (b) { return b.checked; })
            .map(function (b) { return { id: Number(b.value), quantity: 1 }; });
          if (!items.length) return;
          fbtAdd.setAttribute('aria-busy', 'true');
          fetch(AnfaWen.routes.cartAdd + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: items })
          }).then(function (res) { if (!res.ok) throw new Error('add failed'); return AnfaWen.openCartAfterAdd(); })
            .catch(function () {})
            .then(function () { fbtAdd.removeAttribute('aria-busy'); });
        });
      })
      .catch(function () {});
  }

  /* ------------------------------------------------------------------
     Quick view: product summary in a dialog, loaded from
     <product url>?section_id=quick-view. Options resolve to the closest
     variant; Add to cart (AJAX) and Buy now (cart permalink).
  ------------------------------------------------------------------ */
  var qvDialog = document.querySelector('[data-qv-dialog]');
  var qvBody = qvDialog ? qvDialog.querySelector('[data-qv-body]') : null;

  function initQuickView(root) {
    var variants = [];
    try { variants = JSON.parse(root.querySelector('[data-qv-variants]').textContent); } catch (e) { variants = []; }
    var idInput = root.querySelector('[data-qv-variant-id]');
    var priceWrap = root.querySelector('[data-qv-price]');
    var saveBadge = root.querySelector('[data-qv-save]');
    var image = root.querySelector('.qv__image');
    var addBtn = root.querySelector('[data-qv-add]');
    var addLabel = root.querySelector('[data-qv-add-label]');
    var buyBtn = root.querySelector('[data-qv-buy]');
    var details = root.querySelector('[data-qv-details]');
    var status = root.querySelector('[data-qv-status]');
    var groups = Array.prototype.slice.call(root.querySelectorAll('[data-qv-option]'));

    function setImage(src) {
      if (!image || !src) return;
      image.removeAttribute('srcset');
      image.classList.add('is-swapping');
      var next = new Image();
      next.onload = function () { image.src = src; image.classList.remove('is-swapping'); };
      next.onerror = function () { image.classList.remove('is-swapping'); };
      next.src = src;
      Array.prototype.forEach.call(root.querySelectorAll('[data-qv-thumb]'), function (t) {
        t.classList.toggle('is-active', t.getAttribute('data-qv-thumb') === src);
      });
    }
    function apply(variant) {
      if (!variant) return;
      if (idInput) idInput.value = variant.id;
      groups.forEach(function (group) {
        var gi = Number(group.getAttribute('data-qv-option'));
        Array.prototype.forEach.call(group.querySelectorAll('.card-opt__value'), function (el) {
          var val = el.getAttribute('data-value');
          el.setAttribute('aria-pressed', variant.o[gi] === val ? 'true' : 'false');
          el.classList.toggle('is-unavailable', !variants.some(function (v) {
            return v.a && v.o[gi] === val && v.o.every(function (o, oi) { return oi === gi || o === variant.o[oi]; });
          }));
        });
        var selected = group.querySelector('[data-qv-selected]');
        if (selected) selected.textContent = variant.o[gi];
      });
      if (priceWrap) {
        priceWrap.textContent = '';
        var current = document.createElement('span');
        current.className = 'price__current';
        current.textContent = variant.p;
        priceWrap.appendChild(current);
        if (variant.c) {
          var compare = document.createElement('s');
          compare.className = 'price__compare';
          compare.textContent = variant.c;
          priceWrap.appendChild(compare);
        }
        priceWrap.classList.toggle('price--on-sale', !!variant.c);
      }
      if (saveBadge) {
        var save = variant.cc > variant.pc ? Math.floor((variant.cc - variant.pc) * 100 / variant.cc) : 0;
        saveBadge.hidden = save <= 0;
        if (save > 0) saveBadge.textContent = (saveBadge.getAttribute('data-template') || '').replace('__P__', save);
      }
      if (addBtn) {
        addBtn.disabled = !variant.a;
        if (addLabel) addLabel.textContent = addBtn.getAttribute(variant.a ? 'data-label-add' : 'data-label-sold-out');
      }
      if (buyBtn) buyBtn.disabled = !variant.a;
      if (details && variant.u) details.href = variant.u;
      setImage(variant.i);
    }

    root.addEventListener('click', function (event) {
      var thumb = event.target.closest('[data-qv-thumb]');
      if (thumb) { setImage(thumb.getAttribute('data-qv-thumb')); return; }
      var chip = event.target.closest('.card-opt__value');
      if (chip) {
        var gi = Number(chip.closest('[data-qv-option]').getAttribute('data-qv-option'));
        var value = chip.getAttribute('data-value');
        var wanted = groups.map(function (group) {
          var pressed = group.querySelector('[aria-pressed="true"]');
          return pressed ? pressed.getAttribute('data-value') : null;
        });
        wanted[gi] = value;
        /* Exact match, else the closest variant that has the clicked value (available first) */
        var best = null;
        var bestScore = -1;
        variants.forEach(function (v) {
          if (v.o[gi] !== value) return;
          var score = v.a ? 100 : 0;
          v.o.forEach(function (o, oi) { if (o === wanted[oi]) score += 1; });
          if (score > bestScore) { best = v; bestScore = score; }
        });
        apply(best);
        return;
      }
      var qty = function () {
        var input = root.querySelector('.qty input');
        return Math.max(1, Number(input && input.value) || 1);
      };
      if (event.target.closest('[data-qv-buy]')) {
        window.location.href = AnfaWen.routes.cart + '/' + idInput.value + ':' + qty();
        return;
      }
      if (event.target.closest('[data-qv-add]')) {
        addBtn.setAttribute('aria-busy', 'true');
        fetch(AnfaWen.routes.cartAdd + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(idInput.value), quantity: qty() }] })
        })
          .then(function (res) {
            return res.json().then(function (data) { if (!res.ok) throw data; return data; });
          })
          .then(function () {
            qvDialog.close();
            return AnfaWen.openCartAfterAdd();
          })
          .catch(function (error) {
            if (status) status.textContent = (error && (error.description || error.message)) || '';
          })
          .then(function () { addBtn.removeAttribute('aria-busy'); });
      }
    });
  }

  if (qvDialog && qvBody && typeof qvDialog.showModal === 'function') {
    var qvCache = {};
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-quick-view]');
      if (!trigger) return;
      event.preventDefault();
      var url = trigger.getAttribute('data-quick-view');
      var sectionUrl = url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=quick-view';
      qvBody.textContent = '';
      qvBody.classList.add('is-loading');
      qvDialog.showModal();
      document.documentElement.classList.add('lightbox-open');
      (qvCache[sectionUrl] ? Promise.resolve(qvCache[sectionUrl]) : fetch(sectionUrl).then(function (res) {
        if (!res.ok) throw new Error('quick view failed');
        return res.text();
      }))
        .then(function (html) {
          qvCache[sectionUrl] = html;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var content = doc.querySelector('[data-qv]');
          if (!content) throw new Error('empty');
          qvBody.classList.remove('is-loading');
          qvBody.appendChild(document.importNode(content, true));
          initQuickView(qvBody.querySelector('[data-qv]'));
          if (typeof syncWishlistUI === 'function') syncWishlistUI();
        })
        .catch(function () { window.location.href = url; });
    });
    qvDialog.querySelector('[data-qv-close]').addEventListener('click', function () { qvDialog.close(); });
    qvDialog.addEventListener('click', function (event) { if (event.target === qvDialog) qvDialog.close(); });
    qvDialog.addEventListener('close', function () { document.documentElement.classList.remove('lightbox-open'); });
  }

  /* ------------------------------------------------------------------
     Collection: products-per-row switch (remembered) + load more
  ------------------------------------------------------------------ */
  var gridSwitch = document.querySelector('[data-grid-switch]');
  var collectionGrid = document.querySelector('[data-collection-grid]');
  if (gridSwitch && collectionGrid) {
    var mobileQuery = window.matchMedia('(max-width: 749px)');
    var storageKey = function () { return mobileQuery.matches ? 'aw-grid-mobile' : 'aw-grid-desktop'; };
    var setCols = function (cols, save) {
      collectionGrid.setAttribute('data-cols', cols);
      Array.prototype.forEach.call(gridSwitch.querySelectorAll('[data-cols]'), function (b) {
        b.setAttribute('aria-pressed', b.getAttribute('data-cols') === String(cols) ? 'true' : 'false');
      });
      if (save) { try { window.localStorage.setItem(storageKey(), cols); } catch (e) { /* blocked */ } }
    };
    var initialCols = function () {
      var saved = null;
      try { saved = window.localStorage.getItem(storageKey()); } catch (e) { saved = null; }
      return saved || (mobileQuery.matches ? '2' : '4');
    };
    setCols(initialCols(), false);
    gridSwitch.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-cols]');
      if (btn) setCols(btn.getAttribute('data-cols'), true);
    });
    if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', function () { setCols(initialCols(), false); });
  }

  var loadMore = document.querySelector('[data-load-more]');
  if (loadMore && collectionGrid && window.fetch) {
    document.documentElement.classList.add('has-load-more');
    var loadBtn = loadMore.querySelector('[data-load-more-btn]');
    var loadText = loadMore.querySelector('[data-load-more-text]');
    var loadBar = loadMore.querySelector('[data-load-more-bar]');
    loadBtn.addEventListener('click', function () {
      var nextUrl = loadMore.getAttribute('data-next-url');
      if (!nextUrl) return;
      loadBtn.setAttribute('aria-busy', 'true');
      fetch(nextUrl)
        .then(function (res) { if (!res.ok) throw new Error('load more failed'); return res.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var grid = doc.querySelector('[data-collection-grid]');
          if (grid) {
            Array.prototype.forEach.call(grid.children, function (card, i) {
              card.style.setProperty('--reveal-delay', (i % 4) * 70 + 'ms');
              card.classList.add('is-appended');
              collectionGrid.appendChild(document.importNode(card, true));
            });
          }
          var shown = collectionGrid.children.length;
          var total = Number(loadText.getAttribute('data-total')) || shown;
          loadText.textContent = (loadText.getAttribute('data-template') || '').replace('__S__', shown).replace('__T__', total);
          if (loadBar) loadBar.style.inlineSize = Math.min(100, Math.round(shown * 100 / total)) + '%';
          var next = doc.querySelector('[data-load-more]');
          if (next) loadMore.setAttribute('data-next-url', next.getAttribute('data-next-url'));
          else { loadMore.removeAttribute('data-next-url'); loadBtn.hidden = true; }
          try { window.history.replaceState({}, '', nextUrl); } catch (e) { /* ignore */ }
        })
        .catch(function () { window.location.href = nextUrl; })
        .then(function () { loadBtn.removeAttribute('aria-busy'); });
    });
  }

  /* ------------------------------------------------------------------
     Price per piece ("Set of 4" → price / 4), kept in step with variants
  ------------------------------------------------------------------ */
  AnfaWen.updatePerPiece = function (scope, cents) {
    var el = scope && scope.querySelector('.per-piece');
    if (!el || cents === undefined) return;
    var pieces = Number(el.getAttribute('data-pieces')) || 0;
    if (pieces < 2) return;
    el.textContent = (el.getAttribute('data-template') || '').replace('__P__', AnfaWen.formatMoney(Math.round(cents / pieces)));
  };

  /* ------------------------------------------------------------------
     Toast: short confirmations at the bottom of the screen
  ------------------------------------------------------------------ */
  var toastRegion = document.querySelector('[data-toast-region]');
  AnfaWen.toast = function (message) {
    if (!toastRegion || !message) return;
    var toast = document.createElement('div');
    toast.className = 'toast';
    toast.textContent = message;
    toastRegion.appendChild(toast);
    window.setTimeout(function () { toast.classList.add('is-leaving'); }, 2400);
    window.setTimeout(function () { toast.remove(); }, 2800);
  };

  /* Copy a discount code (welcome popup) */
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-copy-code]');
    if (!btn) return;
    var code = btn.getAttribute('data-copy-code');
    var done = function () { AnfaWen.toast((AnfaWen.strings || {}).toastCopied); btn.classList.add('is-copied'); };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(code).then(done, done);
    else done();
  });

  /* ------------------------------------------------------------------
     Welcome popup: after the delay or on exit intent (desktop), at most
     once every 7 days, never again after signing up
  ------------------------------------------------------------------ */
  var welcome = document.querySelector('[data-welcome]');
  if (welcome && typeof welcome.showModal === 'function') {
    var WELCOME_KEY = 'aw-welcome';
    var readWelcome = function () {
      try { return JSON.parse(window.localStorage.getItem(WELCOME_KEY) || '{}'); } catch (e) { return { blocked: true }; }
    };
    var writeWelcome = function (data) {
      try { window.localStorage.setItem(WELCOME_KEY, JSON.stringify(data)); } catch (e) { /* storage blocked */ }
    };
    var state = readWelcome();
    var week = 7 * 24 * 60 * 60 * 1000;
    var canShow = !state.blocked && !state.subscribed && !(state.seen && Date.now() - state.seen < week) &&
      window.location.search.indexOf('customer_posted') === -1;
    var openWelcome = function () {
      if (welcome.open || document.querySelector('dialog[open]') || document.querySelector('.drawer--open')) return;
      writeWelcome({ seen: Date.now() });
      var welcomeImg = welcome.querySelector('[data-src]');
      if (welcomeImg && !welcomeImg.src) welcomeImg.src = welcomeImg.getAttribute('data-src');
      welcome.showModal();
      document.documentElement.classList.add('lightbox-open');
      document.removeEventListener('mouseout', exitIntent);
    };
    var exitIntent = function (event) {
      /* With items in the cart the cart reminder takes exit intent instead */
      if (AnfaWen.cartReminderActive && AnfaWen.cartReminderActive()) return;
      if (!event.relatedTarget && event.clientY <= 0) openWelcome();
    };
    if (canShow) {
      window.setTimeout(openWelcome, (Number(welcome.getAttribute('data-delay')) || 25) * 1000);
      /* Exit intent only once the visitor has been here a little while */
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        window.setTimeout(function () { document.addEventListener('mouseout', exitIntent); }, 8000);
      }
    }
    welcome.addEventListener('click', function (event) {
      if (event.target === welcome || event.target.closest('[data-welcome-close]')) welcome.close();
    });
    welcome.addEventListener('close', function () { document.documentElement.classList.remove('lightbox-open'); });

    var welcomeForm = welcome.querySelector('[data-welcome-form]');
    if (welcomeForm && window.fetch && window.FormData) {
      welcomeForm.addEventListener('submit', function (event) {
        var email = welcomeForm.querySelector('input[type="email"]');
        var error = welcome.querySelector('[data-welcome-error]');
        if (!email.value || !email.checkValidity()) {
          event.preventDefault();
          if (error) error.hidden = false;
          return;
        }
        event.preventDefault();
        var submit = welcomeForm.querySelector('[type="submit"]');
        submit.setAttribute('aria-busy', 'true');
        fetch(welcomeForm.getAttribute('action'), { method: 'POST', body: new FormData(welcomeForm) })
          .then(function (res) {
            /* Shopify may ask for a challenge (captcha): hand over to the normal form post */
            if (res.url && res.url.indexOf('challenge') !== -1) { welcomeForm.submit(); return; }
            if (!res.ok) throw new Error('signup failed');
            writeWelcome({ subscribed: Date.now() });
            welcome.querySelector('[data-welcome-step="form"]').hidden = true;
            welcome.querySelector('[data-welcome-step="done"]').hidden = false;
          })
          .catch(function () { if (error) error.hidden = false; })
          .then(function () { submit.removeAttribute('aria-busy'); });
      });
    }
  }

  /* ------------------------------------------------------------------
     Back to top: appears after a screen of scrolling; ring shows progress
  ------------------------------------------------------------------ */
  var toTop = document.querySelector('[data-to-top]');
  if (toTop) {
    var ring = toTop.querySelector('[data-to-top-ring]');
    var ticking = false;
    var updateToTop = function () {
      ticking = false;
      var max = document.documentElement.scrollHeight - window.innerHeight;
      var y = window.scrollY;
      toTop.hidden = y < window.innerHeight * 0.9;
      if (ring && max > 0) ring.style.strokeDashoffset = String(100 - Math.min(100, (y / max) * 100));
    };
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; window.requestAnimationFrame(updateToTop); }
    }, { passive: true });
    updateToTop();
    toTop.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
  }

  /* ------------------------------------------------------------------
     Homepage motion extras (all skipped for prefers-reduced-motion):
     icons draw their strokes in, category tiles tilt toward the pointer,
     hero scroll cue, slow photo parallax inside brand / B2B sections
  ------------------------------------------------------------------ */
  if (!reduceMotion && 'IntersectionObserver' in window) {
    /* Icon strokes draw in when their block reaches the viewport */
    var drawTargets = document.querySelectorAll('.benefit__icon, .b2b-band__feature-icon, .hero-ed__trust li, .trust-list__icon');
    var drawObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-drawn');
        drawObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px' });
    Array.prototype.forEach.call(drawTargets, function (wrap, wi) {
      var shapes = wrap.querySelectorAll('svg path, svg circle, svg rect, svg line, svg polyline');
      if (!shapes.length) return;
      /* Measure first; a hidden icon reports 0 and would end up dashed, so it is left as is */
      var lengths = [];
      try {
        Array.prototype.forEach.call(shapes, function (shape) { lengths.push(shape.getTotalLength()); });
      } catch (e) { return; }
      if (lengths.some(function (l) { return !(l > 1); })) return;
      Array.prototype.forEach.call(shapes, function (shape, si) {
        shape.style.setProperty('--len', Math.ceil(lengths[si]) + 1);
        shape.style.setProperty('--draw-delay', (si * 90 + (wi % 4) * 120) + 'ms');
      });
      wrap.classList.add('draw-ready');
      drawObserver.observe(wrap);
    });
  }

  if (!reduceMotion && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    /* Category tiles lean toward the pointer with a moving glint */
    Array.prototype.forEach.call(document.querySelectorAll('.category-tile'), function (tile) {
      var media = tile.querySelector('.category-tile__media');
      if (!media) return;
      tile.classList.add('can-tilt');
      tile.addEventListener('pointermove', function (e) {
        var r = media.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        media.style.setProperty('--tilt-x', (py * -7).toFixed(2) + 'deg');
        media.style.setProperty('--tilt-y', (px * 9).toFixed(2) + 'deg');
        media.style.setProperty('--glint-x', ((px + 0.5) * 100).toFixed(1) + '%');
        media.style.setProperty('--glint-y', ((py + 0.5) * 100).toFixed(1) + '%');
      });
      tile.addEventListener('pointerleave', function () {
        media.style.setProperty('--tilt-x', '0deg');
        media.style.setProperty('--tilt-y', '0deg');
      });
    });
  }

  /* Hero scroll cue: glide to the next section, fade out once scrolling starts */
  var heroScroll = document.querySelector('[data-hero-scroll]');
  if (heroScroll) {
    heroScroll.addEventListener('click', function (event) {
      var heroSection = heroScroll.closest('.shopify-section') || heroScroll.closest('section');
      var next = heroSection && heroSection.nextElementSibling;
      if (!next) return;
      event.preventDefault();
      next.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    });
    window.addEventListener('scroll', function () {
      heroScroll.classList.toggle('is-gone', window.scrollY > 80);
    }, { passive: true });
  }

  /* Slow parallax for photos inside the brand and B2B sections */
  if (!reduceMotion && body.classList.contains('has-parallax')) {
    var parallaxImgs = Array.prototype.slice.call(document.querySelectorAll('.brand-intro__media img:not(.brand-intro__leaves), .b2b-band__media img'));
    if (parallaxImgs.length) {
      parallaxImgs.forEach(function (img) { img.classList.add('parallax-img'); });
      var parallaxTicking = false;
      var runParallax = function () {
        parallaxTicking = false;
        var vh = window.innerHeight;
        parallaxImgs.forEach(function (img) {
          var r = img.parentElement.getBoundingClientRect();
          if (r.bottom < -100 || r.top > vh + 100) return;
          var progress = (r.top + r.height / 2 - vh / 2) / vh; /* -1 … 1 around the centre */
          img.style.translate = '0 ' + (progress * -28).toFixed(1) + 'px';
        });
      };
      window.addEventListener('scroll', function () {
        if (!parallaxTicking) { parallaxTicking = true; window.requestAnimationFrame(runParallax); }
      }, { passive: true });
      runParallax();
    }
  }

  /* ------------------------------------------------------------------
     Cart reminder: exit intent (desktop) with items in the cart → small
     dialog listing them with a checkout button, once per session.
     Tab title nudge while the visitor is on another tab.
  ------------------------------------------------------------------ */
  var reminder = document.querySelector('[data-cart-reminder]');
  function cartCountNow() {
    var el = document.querySelector('[data-cart-count]');
    return el ? Number(el.textContent.trim()) || 0 : 0;
  }
  AnfaWen.cartReminderActive = function () { return !!reminder && cartCountNow() > 0; };
  if (reminder && typeof reminder.showModal === 'function') {
    var REMINDER_KEY = 'aw-cart-reminder';
    var reminderShown = function () {
      try { return window.sessionStorage.getItem(REMINDER_KEY) === '1'; } catch (e) { return true; }
    };
    var openReminder = function () {
      if (reminderShown() || cartCountNow() === 0 || document.querySelector('dialog[open]') || document.querySelector('.drawer--open')) return;
      AnfaWen.getJSON(AnfaWen.routes.cart + '.js').then(function (cart) {
        if (!cart.item_count) return;
        try { window.sessionStorage.setItem(REMINDER_KEY, '1'); } catch (e) { /* ignore */ }
        var list = reminder.querySelector('[data-reminder-items]');
        list.textContent = '';
        cart.items.slice(0, 3).forEach(function (item) {
          var li = document.createElement('li');
          li.className = 'cart-reminder__item';
          if (item.image) {
            var img = document.createElement('img');
            img.src = item.image + (item.image.indexOf('?') === -1 ? '?' : '&') + 'width=120';
            img.alt = '';
            img.width = 56;
            img.height = 56;
            li.appendChild(img);
          }
          var info = document.createElement('span');
          info.className = 'cart-reminder__info';
          var title = document.createElement('span');
          title.className = 'cart-reminder__name';
          title.textContent = item.product_title;
          var meta = document.createElement('span');
          meta.className = 'cart-reminder__meta';
          meta.textContent = item.quantity + ' × ' + AnfaWen.formatMoney(item.final_price);
          info.appendChild(title);
          info.appendChild(meta);
          li.appendChild(info);
          list.appendChild(li);
        });
        reminder.querySelector('[data-reminder-total]').textContent = AnfaWen.formatMoney(cart.total_price);
        reminder.showModal();
        document.documentElement.classList.add('lightbox-open');
      }).catch(function () {});
    };
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      window.setTimeout(function () {
        document.addEventListener('mouseout', function (event) {
          if (!event.relatedTarget && event.clientY <= 0) openReminder();
        });
      }, 6000);
    }
    reminder.addEventListener('click', function (event) {
      if (event.target === reminder || event.target.closest('[data-reminder-close]')) reminder.close();
    });
    reminder.addEventListener('close', function () { document.documentElement.classList.remove('lightbox-open'); });

    /* Tab title nudge */
    var originalTitle = document.title;
    var tabTitle = reminder.getAttribute('data-tab-title');
    document.addEventListener('visibilitychange', function () {
      if (document.hidden && cartCountNow() > 0 && tabTitle) document.title = tabTitle;
      else document.title = originalTitle;
    });
  }

  /* ------------------------------------------------------------------
     Product photo magnifier (desktop): the photo zooms where the pointer
     is; a click still opens the full-screen viewer
  ------------------------------------------------------------------ */
  if (!reduceMotion && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    Array.prototype.forEach.call(document.querySelectorAll('.product__media-zoom'), function (frame) {
      var img = frame.querySelector('img');
      if (!img) return;
      frame.classList.add('can-magnify');
      frame.addEventListener('pointermove', function (e) {
        var r = frame.getBoundingClientRect();
        img.style.transformOrigin = ((e.clientX - r.left) / r.width * 100).toFixed(1) + '% ' + ((e.clientY - r.top) / r.height * 100).toFixed(1) + '%';
        frame.classList.add('is-magnified');
      });
      frame.addEventListener('pointerleave', function () { frame.classList.remove('is-magnified'); });
    });
  }

  /* ------------------------------------------------------------------
     Card colour preview on hover (desktop): hovering a swatch shows that
     colour's photo; leaving the options returns to the chosen one
  ------------------------------------------------------------------ */
  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    var previewImage = function (card, src) {
      var img = card.querySelector('.product-card__image');
      if (!img || !src) return;
      if (!img.hasAttribute('data-original-src')) img.setAttribute('data-original-src', img.currentSrc || img.src);
      img.removeAttribute('srcset');
      if (img.src !== src) img.src = src;
    };
    document.addEventListener('mouseover', function (event) {
      var chip = event.target.closest('.card-opt--swatch .card-opt__value');
      if (!chip) return;
      var card = chip.closest('[data-product-card]');
      var json = card && card.querySelector('[data-card-variants]');
      if (!json) return;
      var variants;
      try { variants = JSON.parse(json.textContent); } catch (e) { return; }
      var index = Number(chip.parentNode.getAttribute('data-card-option'));
      var value = chip.getAttribute('data-value');
      var match = variants.filter(function (v) { return v.o[index] === value && v.i; })[0];
      if (match) {
        card.classList.add('has-variant-image');
        previewImage(card, match.i);
      }
    });
    document.addEventListener('mouseout', function (event) {
      var opts = event.target.closest('.card-opts');
      if (!opts || opts.contains(event.relatedTarget)) return;
      var card = opts.closest('[data-product-card]');
      var img = card && card.querySelector('.product-card__image');
      if (!img) return;
      /* Back to the clicked variant's photo, or the original photo if none was chosen */
      var chosen = img.getAttribute('data-variant-src') || img.getAttribute('data-original-src');
      if (chosen && img.src !== chosen) img.src = chosen;
      if (!img.getAttribute('data-variant-src')) card.classList.remove('has-variant-image');
    });
  }

  /* Native share sheet (phones) on the product page */
  var nativeShare = document.querySelector('[data-native-share]');
  if (nativeShare && navigator.share) {
    nativeShare.hidden = false;
    nativeShare.addEventListener('click', function () {
      navigator.share({ title: nativeShare.getAttribute('data-share-title') || document.title, url: window.location.href }).catch(function () {});
    });
  }

  /* Collections on phones: floating "Filter and sort" once the toolbar scrolls away */
  var filterFab = document.querySelector('[data-filter-fab]');
  var facets = document.querySelector('[data-facets]');
  if (filterFab && facets && 'IntersectionObserver' in window) {
    new IntersectionObserver(function (entries) {
      var entry = entries[0];
      filterFab.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
    }).observe(facets);
    filterFab.addEventListener('click', function () {
      facets.open = true;
      var top = facets.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
  }

  /* ------------------------------------------------------------------
     Smart sticky header: the header section sticks (the <header> alone
     could not — its section wrapper is only as tall as itself); it slides
     away while scrolling down and returns as soon as the visitor scrolls up.
     Never hides while a menu, search, drawer or dialog is open.
  ------------------------------------------------------------------ */
  var headerSection = stickyHeader ? stickyHeader.closest('.shopify-section') : null;
  if (headerSection) {
    headerSection.classList.add('header-section--sticky');
    var lastY = window.scrollY;
    var headerTicking = false;
    var busy = function () {
      return document.querySelector('.header [open], .header--search-open, .drawer--open, dialog[open]');
    };
    var updateHeader = function () {
      headerTicking = false;
      var y = window.scrollY;
      var delta = y - lastY;
      if (Math.abs(delta) < 6) return;
      var hide = delta > 0 && y > headerSection.offsetHeight + 240 && !busy();
      headerSection.classList.toggle('is-header-hidden', hide);
      lastY = y;
    };
    window.addEventListener('scroll', function () {
      if (!headerTicking) { headerTicking = true; window.requestAnimationFrame(updateHeader); }
    }, { passive: true });
    /* Keyboard users tabbing into the header always get it back */
    headerSection.addEventListener('focusin', function () { headerSection.classList.remove('is-header-hidden'); });
  }

  /* ------------------------------------------------------------------
     Images fade in once loaded (CSS keeps a fallback so none stay hidden)
  ------------------------------------------------------------------ */
  var markLoaded = function (img) { img.classList.add('is-loaded'); };
  var fadeSelector = '.product-card__image, .category-tile__media img, .collection-card img, .shop-menu__media img, .qv__image, .look__media img';
  Array.prototype.forEach.call(document.querySelectorAll(fadeSelector), function (img) {
    if (img.complete && img.naturalWidth) markLoaded(img);
  });
  document.addEventListener('load', function (event) {
    var img = event.target;
    if (img && img.tagName === 'IMG' && img.matches(fadeSelector)) markLoaded(img);
  }, true);

  /* ------------------------------------------------------------------
     Fly to cart: any add button sends a copy of its product photo
     arcing into the visible bag icon, which bounces as it lands
  ------------------------------------------------------------------ */
  var flyToCart = function (img) {
    if (reduceMotion || !img || !img.currentSrc) return;
    /* The bag icon actually on screen: the header one, or the bottom nav
       one on phones when the smart header has slid away */
    var targets = Array.prototype.filter.call(document.querySelectorAll('[data-cart-link], .mobile-nav__item[aria-controls="CartDrawer"]'), function (el) {
      if (el.offsetParent === null) return false;
      var box = el.getBoundingClientRect();
      return box.bottom > 0 && box.top < window.innerHeight;
    });
    var target = targets[0];
    var lift = 0;
    var navCart = document.querySelector('.mobile-nav__item[aria-controls="CartDrawer"]');
    if (!target && navCart && navCart.offsetParent !== null && body.classList.contains('is-scrolling-down')) {
      /* Bottom nav was tucked away: bring it back and aim where it settles */
      body.classList.remove('is-scrolling-down');
      target = navCart;
      lift = navCart.closest('.mobile-nav').offsetHeight + 24;
    }
    var from = img.getBoundingClientRect();
    if (!target || !from.width) return;
    var to = target.getBoundingClientRect();
    if (lift) to = { left: to.left, width: to.width, top: to.top - lift, height: to.height };
    var size = Math.min(from.width, from.height, 180);
    var clone = document.createElement('img');
    clone.src = img.currentSrc;
    clone.alt = '';
    clone.className = 'fly-to-cart';
    clone.style.cssText = 'left:' + (from.left + from.width / 2 - size / 2) + 'px;top:' + (from.top + from.height / 2 - size / 2) + 'px;width:' + size + 'px;height:' + size + 'px';
    document.body.appendChild(clone);
    var dx = to.left + to.width / 2 - (from.left + from.width / 2);
    var dy = to.top + to.height / 2 - (from.top + from.height / 2);
    var flight = clone.animate([
      { transform: 'translate(0, 0) scale(1)', opacity: 1 },
      { transform: 'translate(' + dx * 0.45 + 'px,' + (dy * 0.45 - 90) + 'px) scale(0.55) rotate(-8deg)', opacity: 0.95, offset: 0.5 },
      { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(0.08) rotate(12deg)', opacity: 0.3 }
    ], { duration: 720, easing: 'cubic-bezier(0.45, 0, 0.2, 1)' });
    flight.onfinish = function () {
      clone.remove();
      target.classList.remove('is-catching');
      void target.offsetWidth;
      target.classList.add('is-catching');
      window.setTimeout(function () { target.classList.remove('is-catching'); }, 650);
    };
  };
  AnfaWen.flyToCart = flyToCart;
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-add-button], [data-card-add], [data-sticky-add], [data-qv-add], [data-look-add], [data-fbt-add]');
    if (!button || button.disabled) return;
    var img = null;
    if (button.matches('[data-add-button], [data-sticky-add]')) {
      img = document.querySelector('.product__media-item:not(.hidden) img');
    } else if (button.matches('[data-qv-add]')) {
      img = document.querySelector('.qv__image');
    } else {
      var scope = button.closest('[data-product-card], .look, .fbt, section');
      img = scope && scope.querySelector('.product-card__image, .look__media img, img');
    }
    flyToCart(img);
  });

  /* ------------------------------------------------------------------
     Product cards: cursor spotlight + gentle tilt (desktop pointers)
  ------------------------------------------------------------------ */
  if (!reduceMotion && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    document.addEventListener('pointermove', function (event) {
      var card = event.target.closest && event.target.closest('.product-card');
      if (!card) return;
      var r = card.getBoundingClientRect();
      var px = (event.clientX - r.left) / r.width;
      var py = (event.clientY - r.top) / r.height;
      card.style.setProperty('--mx', (px * 100).toFixed(1) + '%');
      card.style.setProperty('--my', (py * 100).toFixed(1) + '%');
      card.style.setProperty('--ry', ((px - 0.5) * 13).toFixed(2) + 'deg');
      card.style.setProperty('--rx', ((0.5 - py) * 11).toFixed(2) + 'deg');
      /* The photo drifts the other way for depth; the shadow falls away from the cursor */
      card.style.setProperty('--ix', ((0.5 - px) * 14).toFixed(1) + 'px');
      card.style.setProperty('--iy', ((0.5 - py) * 14).toFixed(1) + 'px');
      card.style.setProperty('--sx', ((0.5 - px) * 26).toFixed(1) + 'px');
      card.style.setProperty('--sy', (18 + (0.5 - py) * 18).toFixed(1) + 'px');
      card.classList.add('is-tilting');
    }, { passive: true });
    document.addEventListener('pointerout', function (event) {
      var card = event.target.closest && event.target.closest('.product-card');
      if (card && !card.contains(event.relatedTarget)) card.classList.remove('is-tilting');
    });
  }

  /* ------------------------------------------------------------------
     Button ripple from the click point
  ------------------------------------------------------------------ */
  if (!reduceMotion) {
    document.addEventListener('pointerdown', function (event) {
      var btn = event.target.closest('.btn');
      if (!btn || btn.disabled) return;
      var r = btn.getBoundingClientRect();
      var d = Math.max(r.width, r.height) * 2.2;
      var ripple = document.createElement('span');
      ripple.className = 'btn-ripple';
      ripple.setAttribute('aria-hidden', 'true');
      ripple.style.cssText = 'width:' + d + 'px;height:' + d + 'px;left:' + (event.clientX - r.left - d / 2) + 'px;top:' + (event.clientY - r.top - d / 2) + 'px';
      if (window.getComputedStyle(btn).position === 'static') btn.style.position = 'relative';
      var host = btn.querySelector(':scope > .btn-ripple-host');
      if (!host) { host = document.createElement('span'); host.className = 'btn-ripple-host'; host.setAttribute('aria-hidden', 'true'); btn.insertBefore(host, btn.firstChild); }
      host.appendChild(ripple);
      window.setTimeout(function () { ripple.remove(); }, 700);
    });
  }

  /* Section titles rise in and draw their ornament line */
  if ('IntersectionObserver' in window && !reduceMotion) {
    var headObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        headObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    Array.prototype.forEach.call(document.querySelectorAll('.section-head:not([data-reveal])'), function (head) {
      var box = head.getBoundingClientRect();
      if (box.top < window.innerHeight) return;
      head.setAttribute('data-reveal', '');
      headObserver.observe(head);
    });
  }

  /* ------------------------------------------------------------------
     Accordions (FAQ, product details) open and close smoothly
  ------------------------------------------------------------------ */
  if (!reduceMotion && Element.prototype.animate) {
    document.addEventListener('click', function (event) {
      var summary = event.target.closest('.faq__item > summary, .product__accordion > summary');
      if (!summary) return;
      var details = summary.parentNode;
      if (details.classList.contains('is-animating-details')) { event.preventDefault(); return; }
      event.preventDefault();
      var startHeight = details.offsetHeight;
      var closing = details.open;
      details.classList.add('is-animating-details');
      if (!closing) details.open = true;
      var endHeight = closing ? summary.offsetHeight + (details.offsetHeight - details.clientHeight) + parseFloat(window.getComputedStyle(details).paddingTop) + parseFloat(window.getComputedStyle(details).paddingBottom) : details.offsetHeight;
      var anim = details.animate({ height: [startHeight + 'px', endHeight + 'px'] }, { duration: 340, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' });
      anim.onfinish = function () {
        if (closing) details.open = false;
        details.classList.remove('is-animating-details');
      };
    });
  }

  /* ------------------------------------------------------------------
     Stats count up when they come into view ("25+", "10,000", "98%")
  ------------------------------------------------------------------ */
  var statValues = document.querySelectorAll('.brand-intro__stat-value');
  if (statValues.length && !reduceMotion && 'IntersectionObserver' in window) {
    var countUp = function (el) {
      var match = el.textContent.trim().match(/^(\D*?)(\d[\d,]*(?:\.\d+)?)(.*)$/);
      if (!match) return;
      var prefix = match[1], suffix = match[3], raw = match[2];
      var target = parseFloat(raw.replace(/,/g, ''));
      var decimals = (raw.split('.')[1] || '').length;
      var grouped = raw.indexOf(',') !== -1;
      var format = function (n) {
        var s = n.toFixed(decimals);
        return grouped ? s.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : s;
      };
      var start = null;
      var step = function (now) {
        if (start === null) start = now;
        var t = Math.min(1, (now - start) / 1600);
        var eased = 1 - Math.pow(1 - t, 3);
        el.textContent = prefix + format(target * eased) + suffix;
        if (t < 1) window.requestAnimationFrame(step);
      };
      el.setAttribute('aria-label', el.textContent.trim());
      window.requestAnimationFrame(step);
    };
    var statObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        countUp(entry.target);
        statObserver.unobserve(entry.target);
      });
    }, { threshold: 0.6 });
    Array.prototype.forEach.call(statValues, function (el) { statObserver.observe(el); });
  }

  /* Testimonials rise in, staggered, and light their stars */
  if (!reduceMotion && 'IntersectionObserver' in window) {
    var quoteObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        quoteObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });
    Array.prototype.forEach.call(document.querySelectorAll('.testimonial:not([data-reveal])'), function (card, i) {
      if (card.getBoundingClientRect().top < window.innerHeight) return;
      card.setAttribute('data-reveal', '');
      card.style.setProperty('--reveal-delay', (i % 3) * 110 + 'ms');
      quoteObserver.observe(card);
    });
  }

  /* ------------------------------------------------------------------
     Free shipping bar: fill grows from its previous width after every
     cart update; reaching the threshold sets off a small confetti burst
  ------------------------------------------------------------------ */
  var shipDrawer = document.getElementById('CartDrawer');
  var shipState = function () {
    var bar = document.querySelector('#CartDrawer .ship-progress');
    if (!bar) return null;
    var fill = bar.querySelector('.ship-progress__fill');
    return { bar: bar, fill: fill, pct: fill ? parseFloat(fill.style.inlineSize) || 0 : 0, done: bar.classList.contains('ship-progress--done') };
  };
  var confetti = function (host) {
    var colors = ['#105D4D', '#4E8274', '#7B9E90', '#C35820', '#F1C27D'];
    for (var i = 0; i < 26; i++) {
      var bit = document.createElement('span');
      bit.className = 'aw-confetti';
      bit.setAttribute('aria-hidden', 'true');
      bit.style.background = colors[i % colors.length];
      host.appendChild(bit);
      var angle = Math.random() * Math.PI * 2;
      var dist = 50 + Math.random() * 90;
      var a = bit.animate([
        { transform: 'translate(-50%, -50%) rotate(0deg)', opacity: 1 },
        { transform: 'translate(' + Math.cos(angle) * dist + 'px,' + (Math.sin(angle) * dist - 40) + 'px) rotate(' + (Math.random() * 720 - 360) + 'deg)', opacity: 1, offset: 0.6 },
        { transform: 'translate(' + Math.cos(angle) * dist * 1.2 + 'px,' + (Math.sin(angle) * dist + 50) + 'px) rotate(' + (Math.random() * 720) + 'deg)', opacity: 0 }
      ], { duration: 1100 + Math.random() * 500, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)' });
      a.onfinish = (function (el) { return function () { el.remove(); }; })(bit);
    }
  };
  if (shipDrawer && 'MutationObserver' in window && !reduceMotion) {
    var lastShip = shipState();
    new MutationObserver(function () {
      var now = shipState();
      if (!now || (lastShip && now.bar === lastShip.bar)) { if (now) lastShip = now; return; }
      if (lastShip && now.fill && now.pct !== lastShip.pct) {
        now.fill.animate({ inlineSize: [lastShip.pct + '%', now.pct + '%'] }, { duration: 700, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' });
      }
      if (lastShip && now.done && !lastShip.done) window.setTimeout(function () { confetti(now.bar); }, 350);
      lastShip = now;
    }).observe(shipDrawer, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------
     Add buttons confirm with "✓ Added" once the bag has refreshed
  ------------------------------------------------------------------ */
  var lastAddButton = null;
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-add-button], [data-card-add], [data-sticky-add], [data-qv-add], [data-look-add], [data-fbt-add]');
    if (button && !button.disabled) lastAddButton = button;
  }, true);
  var openAfterAdd = AnfaWen.openCartAfterAdd;
  AnfaWen.openCartAfterAdd = function () {
    var result = openAfterAdd.apply(this, arguments);
    var button = lastAddButton;
    lastAddButton = null;
    Promise.resolve(result).then(function () {
      if (!button || !button.isConnected) return;
      if (window.getComputedStyle(button).position === 'static') button.style.position = 'relative';
      button.setAttribute('data-added-label', (AnfaWen.strings && AnfaWen.strings.added) || '');
      button.classList.add('is-added');
      window.setTimeout(function () { button.classList.remove('is-added'); }, 1800);
    });
    return result;
  };

  /* Price flips in when the variant changes (product page + sticky bar) */
  if (!reduceMotion && 'MutationObserver' in window) {
    Array.prototype.forEach.call(document.querySelectorAll('.product__info [data-price-current], [data-sticky-price]'), function (price) {
      var last = price.textContent;
      new MutationObserver(function () {
        if (price.textContent === last) return;
        last = price.textContent;
        price.classList.remove('is-price-changing');
        void price.offsetWidth;
        price.classList.add('is-price-changing');
      }).observe(price, { childList: true, characterData: true, subtree: true });
      price.addEventListener('animationend', function () { price.classList.remove('is-price-changing'); });
    });
  }

  /* Hero buttons drift toward the cursor (desktop) */
  if (!reduceMotion && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    Array.prototype.forEach.call(document.querySelectorAll('.hero-ed__buttons .btn'), function (btn) {
      btn.addEventListener('pointermove', function (event) {
        var r = btn.getBoundingClientRect();
        btn.style.setProperty('--tx', ((event.clientX - r.left - r.width / 2) * 0.18).toFixed(1) + 'px');
        btn.style.setProperty('--ty', ((event.clientY - r.top - r.height / 2) * 0.3).toFixed(1) + 'px');
      });
      btn.addEventListener('pointerleave', function () {
        btn.style.removeProperty('--tx');
        btn.style.removeProperty('--ty');
      });
    });
  }

  /* "/" jumps to the header search (desktop), with a small hint in the field */
  var headerSearchInput = document.querySelector('.header-search__input');
  if (headerSearchInput) {
    var kbd = document.createElement('kbd');
    kbd.className = 'search-kbd';
    kbd.setAttribute('aria-hidden', 'true');
    kbd.textContent = '/';
    headerSearchInput.insertAdjacentElement('afterend', kbd);
    document.addEventListener('keydown', function (event) {
      if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) return;
      var t = event.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (headerSearchInput.offsetParent === null) return;
      event.preventDefault();
      headerSearchInput.focus();
    });
  }

  /* ------------------------------------------------------------------
     Product page: glow behind the gallery takes the current photo's colours
  ------------------------------------------------------------------ */
  var glowGallery = document.querySelector('.product__gallery');
  if (glowGallery && !reduceMotion) {
    var lastGlow = '';
    var setGlow = function () {
      var img = glowGallery.querySelector('.product__media-item:not(.hidden) img');
      var src = img && (img.currentSrc || img.src);
      if (!src || src === lastGlow) return;
      var small = src.replace(/([?&])width=\d+/, '$1width=200');
      lastGlow = src;
      glowGallery.classList.add('is-glow-changing');
      window.setTimeout(function () {
        glowGallery.style.setProperty('--aw-glow', 'url("' + small.replace(/"/g, '%22') + '")');
        glowGallery.classList.remove('is-glow-changing');
      }, 250);
    };
    setGlow();
    if ('MutationObserver' in window) {
      new MutationObserver(setGlow).observe(glowGallery, { attributes: true, attributeFilter: ['class', 'src', 'srcset'], subtree: true });
    }
    glowGallery.addEventListener('load', setGlow, true);
  }

  /* ------------------------------------------------------------------
     Phones: swipe a product card photo to flip to its second photo
  ------------------------------------------------------------------ */
  if (window.matchMedia('(hover: none)').matches) {
    var addDots = function (scope) {
      Array.prototype.forEach.call((scope || document).querySelectorAll('.product-card__media'), function (media) {
        if (!media.querySelector('.product-card__image-hover') || media.querySelector('.card-dots')) return;
        var dots = document.createElement('div');
        dots.className = 'card-dots';
        dots.setAttribute('aria-hidden', 'true');
        dots.innerHTML = '<span class="is-active"></span><span></span>';
        media.appendChild(dots);
      });
    };
    addDots();
    if ('MutationObserver' in window) {
      new MutationObserver(function (records) {
        records.forEach(function (r) { Array.prototype.forEach.call(r.addedNodes, function (n) { if (n.nodeType === 1) addDots(n); }); });
      }).observe(document.body, { childList: true, subtree: true });
    }
    var swipeStart = null;
    var swipedAt = 0;
    document.addEventListener('touchstart', function (event) {
      var media = event.target.closest('.product-card__media');
      swipeStart = media && media.querySelector('.card-dots') ? { media: media, x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    }, { passive: true });
    document.addEventListener('touchend', function (event) {
      if (!swipeStart) return;
      var t = event.changedTouches[0];
      var dx = t.clientX - swipeStart.x;
      var dy = t.clientY - swipeStart.y;
      var media = swipeStart.media;
      swipeStart = null;
      if (Math.abs(dx) < 35 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
      var card = media.closest('.product-card');
      if (!card || card.classList.contains('has-variant-image')) return;
      card.classList.toggle('show-alt');
      var on = card.classList.contains('show-alt');
      var spans = media.querySelectorAll('.card-dots span');
      if (spans.length === 2) { spans[0].classList.toggle('is-active', !on); spans[1].classList.toggle('is-active', on); }
      swipedAt = Date.now();
    }, { passive: true });
    /* A swipe must not also open the product */
    document.addEventListener('click', function (event) {
      if (Date.now() - swipedAt < 450 && event.target.closest('.product-card__media')) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);
  }

  /* ------------------------------------------------------------------
     Phones: bottom nav hides while scrolling down, returns on scroll up
     and near the end of the page
  ------------------------------------------------------------------ */
  if (document.querySelector('.mobile-nav')) {
    var navLastY = window.scrollY;
    var navTicking = false;
    var updateNav = function () {
      navTicking = false;
      var y = window.scrollY;
      var delta = y - navLastY;
      if (Math.abs(delta) < 8) return;
      var nearEnd = y + window.innerHeight > document.documentElement.scrollHeight - 120;
      body.classList.toggle('is-scrolling-down', delta > 0 && y > 320 && !nearEnd);
      navLastY = y;
    };
    window.addEventListener('scroll', function () {
      if (!navTicking) { navTicking = true; window.requestAnimationFrame(updateNav); }
    }, { passive: true });
  }

  /* Short haptic tap on Android when something goes in the bag or wishlist */
  document.addEventListener('click', function (event) {
    if (!navigator.vibrate || !window.matchMedia('(hover: none)').matches) return;
    var hit = event.target.closest('[data-add-button], [data-card-add], [data-sticky-add], [data-qv-add], [data-look-add], [data-fbt-add], [data-wishlist-toggle]');
    if (hit && !hit.disabled) { try { navigator.vibrate(12); } catch (e) { /* ignore */ } }
  });

  /* ------------------------------------------------------------------
     Phones: drawers follow the finger and close with a swipe toward
     the edge they came from
  ------------------------------------------------------------------ */
  if (window.matchMedia('(hover: none)').matches) {
    var drag = null;
    var scrollsSideways = function (el, stop) {
      for (; el && el !== stop; el = el.parentElement) {
        if (el.scrollWidth > el.clientWidth + 2) {
          var ox = window.getComputedStyle(el).overflowX;
          if (ox === 'auto' || ox === 'scroll') return true;
        }
      }
      return false;
    };
    document.addEventListener('touchstart', function (event) {
      var drawer = event.target.closest('.drawer.drawer--open');
      if (!drawer || event.touches.length > 1 || event.target.closest('input, textarea, select, [type="range"]') || scrollsSideways(event.target, drawer)) { drag = null; return; }
      var hiddenX = window.getComputedStyle(drawer).getPropertyValue('--drawer-hidden-x').trim();
      drag = { drawer: drawer, x: event.touches[0].clientX, y: event.touches[0].clientY, dir: hiddenX.charAt(0) === '-' ? -1 : 1, dx: 0, active: false, t: Date.now() };
    }, { passive: true });
    document.addEventListener('touchmove', function (event) {
      if (!drag) return;
      var dx = event.touches[0].clientX - drag.x;
      var dy = event.touches[0].clientY - drag.y;
      if (!drag.active) {
        if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) { drag = null; return; }
        if (Math.abs(dx) < 10) return;
        drag.active = true;
        drag.drawer.classList.add('is-dragging');
      }
      drag.dx = dx * drag.dir > 0 ? dx : dx * 0.15;
      drag.drawer.style.transform = 'translateX(' + drag.dx + 'px)';
    }, { passive: true });
    document.addEventListener('touchend', function () {
      if (!drag) return;
      var d = drag;
      drag = null;
      if (!d.active) return;
      var moved = d.dx * d.dir;
      var fast = moved > 40 && Date.now() - d.t < 250;
      d.drawer.classList.remove('is-dragging');
      d.drawer.style.transform = '';
      if (moved > d.drawer.offsetWidth * 0.3 || fast) closeDrawer(d.drawer);
    });
  }

  /* ------------------------------------------------------------------
     Header search: remembers the last five searches on this device and
     offers them when the panel opens
  ------------------------------------------------------------------ */
  var searchForm = document.querySelector('[data-header-search] [data-predictive-search]');
  var searchSuggest = document.querySelector('[data-header-search] [data-search-suggest]');
  if (searchForm && searchSuggest) {
    var RECENT_KEY = 'aw-recent-searches';
    var searchField = searchForm.querySelector('input[name="q"]');
    var readRecent = function () {
      try { return JSON.parse(window.localStorage.getItem(RECENT_KEY)) || []; } catch (e) { return []; }
    };
    var writeRecent = function (list) {
      try { window.localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch (e) { /* ignore */ }
    };
    var remember = function (q) {
      q = (q || '').trim();
      if (q.length < 2) return;
      var list = readRecent().filter(function (item) { return item.toLowerCase() !== q.toLowerCase(); });
      list.unshift(q);
      writeRecent(list.slice(0, 5));
    };
    var renderRecent = function () {
      var old = searchSuggest.querySelector('.recent-searches');
      if (old) old.remove();
      var list = readRecent();
      if (!list.length) return;
      var box = document.createElement('div');
      box.className = 'recent-searches';
      var head = document.createElement('div');
      head.className = 'recent-searches__head';
      var title = document.createElement('span');
      title.textContent = (AnfaWen.strings && AnfaWen.strings.recentSearches) || '';
      var clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'recent-searches__clear';
      clear.textContent = (AnfaWen.strings && AnfaWen.strings.clearAll) || '';
      clear.addEventListener('click', function () { writeRecent([]); box.remove(); if (searchField) searchField.focus(); });
      head.appendChild(title);
      head.appendChild(clear);
      var ul = document.createElement('ul');
      ul.className = 'recent-searches__list';
      list.forEach(function (q) {
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.className = 'recent-searches__item';
        a.href = searchForm.getAttribute('action') + '?q=' + encodeURIComponent(q);
        a.innerHTML = '<svg class="icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>';
        a.appendChild(document.createTextNode(q));
        li.appendChild(a);
        ul.appendChild(li);
      });
      box.appendChild(head);
      box.appendChild(ul);
      searchSuggest.insertBefore(box, searchSuggest.firstChild);
    };
    searchForm.addEventListener('submit', function () { if (searchField) remember(searchField.value); });
    /* Picking a live result counts as searching for what was typed */
    document.addEventListener('click', function (event) {
      if (event.target.closest('[data-predictive-search-results] a') && searchField) remember(searchField.value);
    });
    if (searchField) searchField.addEventListener('focus', renderRecent);
    renderRecent();
  }

  /* Product page: ← / → step through the photos (desktop keyboards) */
  var galleryPrev = document.querySelector('[data-gallery-prev]');
  var galleryNext = document.querySelector('[data-gallery-next]');
  if (galleryPrev && galleryNext) {
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.altKey || event.ctrlKey || event.metaKey || document.querySelector('dialog[open], .drawer--open')) return;
      var t = event.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      var rtl = document.documentElement.dir === 'rtl';
      var forward = (event.key === 'ArrowRight') !== rtl;
      var gallery = galleryPrev.closest('.product__gallery');
      var box = gallery && gallery.getBoundingClientRect();
      if (!box || box.bottom < 0 || box.top > window.innerHeight) return;
      (forward ? galleryNext : galleryPrev).click();
    });
  }

  /* ------------------------------------------------------------------
     Phones: drag the quick view sheet down to close it
  ------------------------------------------------------------------ */
  var sheet = document.querySelector('[data-qv-dialog]');
  if (sheet && window.matchMedia('(hover: none)').matches) {
    var sheetDrag = null;
    sheet.addEventListener('close', function () { sheet.classList.remove('is-dragged'); sheet.style.transform = ''; });
    sheet.addEventListener('touchstart', function (event) {
      var bodyEl = sheet.querySelector('.qv-dialog__body');
      if (!window.matchMedia('(max-width: 749px)').matches || (bodyEl && bodyEl.scrollTop > 0) || event.target.closest('input, select, textarea, .qv__thumbs')) { sheetDrag = null; return; }
      sheetDrag = { y: event.touches[0].clientY, x: event.touches[0].clientX, dy: 0, active: false, t: Date.now() };
    }, { passive: true });
    sheet.addEventListener('touchmove', function (event) {
      if (!sheetDrag) return;
      var dy = event.touches[0].clientY - sheetDrag.y;
      var dx = event.touches[0].clientX - sheetDrag.x;
      if (!sheetDrag.active) {
        if (dy < 8 || Math.abs(dx) > Math.abs(dy)) { if (dy < -4 || Math.abs(dx) > 10) sheetDrag = null; return; }
        sheetDrag.active = true;
        sheet.classList.add('is-dragging', 'is-dragged');
      }
      sheetDrag.dy = Math.max(0, dy);
      sheet.style.transform = 'translateY(' + sheetDrag.dy + 'px)';
    }, { passive: true });
    sheet.addEventListener('touchend', function () {
      if (!sheetDrag) return;
      var d = sheetDrag;
      sheetDrag = null;
      if (!d.active) return;
      sheet.classList.remove('is-dragging');
      sheet.classList.add('is-settling');
      var close = d.dy > sheet.offsetHeight * 0.25 || (d.dy > 50 && Date.now() - d.t < 250);
      sheet.style.transform = close ? 'translateY(100%)' : '';
      window.setTimeout(function () {
        sheet.classList.remove('is-settling');
        if (close) { sheet.close(); sheet.style.transform = ''; }
      }, 280);
    });
  }

  /* ------------------------------------------------------------------
     Product cards: a click on any empty part of the card opens the
     product (the title link's current URL, so a chosen variant carries
     over). Buttons, chips and links inside keep their own job; Ctrl/⌘ or
     middle click opens a new tab; selecting text never navigates.
  ------------------------------------------------------------------ */
  var cardTarget = function (event) {
    var card = event.target.closest('.product-card');
    if (!card || event.target.closest('a, button, input, select, textarea, label, summary, [role="button"], [data-card-variants]')) return null;
    var selection = window.getSelection && window.getSelection();
    if (selection && String(selection).length > 0) return null;
    var link = card.querySelector('.product-card__title a');
    return link ? link.href : null;
  };
  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0) return;
    var url = cardTarget(event);
    if (!url) return;
    if (event.ctrlKey || event.metaKey || event.shiftKey) window.open(url, '_blank', 'noopener');
    else window.location.href = url;
  });
  document.addEventListener('auxclick', function (event) {
    if (event.button !== 1) return;
    var url = cardTarget(event);
    if (url) { event.preventDefault(); window.open(url, '_blank', 'noopener'); }
  });

  /* Footer back-to-top */
  Array.prototype.forEach.call(document.querySelectorAll('[data-back-to-top]'), function (link) {
    link.addEventListener('click', function (event) {
      event.preventDefault();
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    });
  });

  /* ------------------------------------------------------------------
     Product finder: filter server-rendered cards by option values
  ------------------------------------------------------------------ */
  Array.prototype.forEach.call(document.querySelectorAll('[data-finder]'), function (finder) {
    var items = finder.querySelectorAll('[data-finder-item]');
    var count = finder.querySelector('[data-finder-count]');
    var empty = finder.querySelector('[data-finder-empty]');
    var selected = { 1: '', 2: '' };

    function apply() {
      var shown = 0;
      Array.prototype.forEach.call(items, function (item) {
        var match = ['1', '2'].every(function (g) {
          return !selected[g] || (item.getAttribute('data-v' + g) || '').indexOf('|' + selected[g] + '|') !== -1;
        });
        item.hidden = !match;
        if (match) shown++;
      });
      if (count) count.textContent = (count.getAttribute('data-label') || '').replace('__COUNT__', shown);
      if (empty) empty.hidden = shown > 0;
    }

    function press(group, value) {
      selected[group.getAttribute('data-finder-group')] = value;
      Array.prototype.forEach.call(group.querySelectorAll('[data-finder-value]'), function (btn) {
        btn.setAttribute('aria-pressed', btn.getAttribute('data-finder-value') === value ? 'true' : 'false');
      });
    }

    finder.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-finder-value]');
      if (btn) {
        press(btn.closest('[data-finder-group]'), btn.getAttribute('data-finder-value'));
        apply();
        return;
      }
      if (event.target.closest('[data-finder-reset]')) {
        Array.prototype.forEach.call(finder.querySelectorAll('[data-finder-group]'), function (group) { press(group, ''); });
        apply();
      }
    });
    apply();
  });

  /* ------------------------------------------------------------------
     Tabs (WAI-ARIA tabs pattern: click + arrow keys)
  ------------------------------------------------------------------ */
  Array.prototype.forEach.call(document.querySelectorAll('[data-tabs]'), function (wrap) {
    var tabs = Array.prototype.slice.call(wrap.querySelectorAll('[role="tab"]'));
    function select(tab, focus) {
      tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        var panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      if (focus) tab.focus();
    }
    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { select(tab, false); });
      tab.addEventListener('keydown', function (event) {
        var rtl = document.documentElement.dir === 'rtl';
        var next = { ArrowRight: rtl ? -1 : 1, ArrowLeft: rtl ? 1 : -1 }[event.key];
        if (event.key === 'Home') next = -i;
        if (event.key === 'End') next = tabs.length - 1 - i;
        if (next === undefined) return;
        event.preventDefault();
        select(tabs[(i + next + tabs.length) % tabs.length], true);
      });
    });
  });

  /* ------------------------------------------------------------------
     Shop the look: hotspot highlight + add every listed variant
  ------------------------------------------------------------------ */
  Array.prototype.forEach.call(document.querySelectorAll('[data-look]'), function (look) {
    function activate(index) {
      Array.prototype.forEach.call(look.querySelectorAll('[data-look-spot]'), function (s) {
        s.classList.toggle('is-active', s.getAttribute('data-look-spot') === index);
      });
      Array.prototype.forEach.call(look.querySelectorAll('[data-look-item]'), function (item) {
        item.classList.toggle('is-active', item.getAttribute('data-look-item') === index);
      });
    }
    /* Hovering a row or a dot points out its partner; the other dots dim */
    look.addEventListener('mouseover', function (event) {
      var target = event.target.closest('[data-look-item], [data-look-spot]');
      if (!target) return;
      activate(target.getAttribute('data-look-item') || target.getAttribute('data-look-spot'));
      look.classList.add('is-pointing');
    });
    look.addEventListener('mouseleave', function () { look.classList.remove('is-pointing'); });
    look.addEventListener('focusin', function (event) {
      var row = event.target.closest('[data-look-item]');
      if (row) activate(row.getAttribute('data-look-item'));
    });

    look.addEventListener('click', function (event) {
      var spot = event.target.closest('[data-look-spot]');
      if (spot) {
        /* Mouse: open the product. Touch: first tap shows the piece, second tap opens it */
        var touchFirstTap = event.pointerType === 'touch' && !spot.classList.contains('is-active');
        activate(spot.getAttribute('data-look-spot'));
        if (!touchFirstTap && spot.getAttribute('data-look-url')) window.location.href = spot.getAttribute('data-look-url');
        return;
      }
      /* Whole row opens the product (the title link stays the keyboard target) */
      var row = event.target.closest('[data-look-item]');
      if (row && !event.target.closest('a, button')) {
        window.location.href = row.getAttribute('data-look-url');
        return;
      }
      var addAll = event.target.closest('[data-look-add]');
      if (!addAll) return;
      var ids = addAll.getAttribute('data-look-add').split(',').filter(Boolean);
      addAll.setAttribute('aria-busy', 'true');
      addAll.disabled = true;
      fetch(AnfaWen.routes.cartAdd + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: ids.map(function (id) { return { id: Number(id), quantity: 1 }; }) })
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) throw data;
            return data;
          });
        })
        .then(function () { return AnfaWen.openCartAfterAdd(); })
        .catch(function (error) {
          showCartStatus((error && (error.description || error.message)) || 'Cart error', true);
        })
        .then(function () {
          addAll.removeAttribute('aria-busy');
          addAll.disabled = false;
        });
    });
  });

  /* ------------------------------------------------------------------
     Shared utilities used by later phases
  ------------------------------------------------------------------ */
  AnfaWen.openDrawer = openDrawer;
  AnfaWen.closeDrawer = closeDrawer;

  /* Update every cart count bubble on the page */
  AnfaWen.updateCartCount = function (count) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-cart-count]'), function (el) {
      var grew = count > (Number(el.textContent) || 0);
      el.textContent = count > 0 ? String(count) : '';
      if (grew) {
        el.classList.remove('is-bumped');
        void el.offsetWidth;
        el.classList.add('is-bumped');
      }
    });
  };


})();
